/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './App.css';
import GTFSExplorer from './GTFSExplorer';


// ─── API base (env-aware) ───────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'https://gxqbqpargppbzwceamea.supabase.co/functions/v1/db-proxy';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY || '';
const apiFetch = (url) => fetch(url, { headers: { 'Authorization': 'Bearer ' + SUPABASE_ANON_KEY } });

// ─── Error tracking (Sentry-ready) ──────────────────────────────────────────
const trackError = (context, err) => {
  console.error(`[${context}]`, err?.message || err);
  window.Sentry?.captureException(err, { tags: { context } });
};

// ─── Analytics: Umami ────────────────────────────────────────────────────────
const trackEvent = (name, props) => {
  window.umami?.track(name, props);
  if (process.env.NODE_ENV === 'development') console.log('[analytics]', name, props);
};
// ─── Leaflet icon fix ───────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ─── Constants ──────────────────────────────────────────────────────────────
const VIDEOS = ['video']; // placeholder — length only, src is API_BASE

const PERSON_COLORS = ['#C2410C','#1D4ED8','#7C3AED','#047857'];

// TripAdvisor destination IDs for deep-linking
const TA = {
  'Berlin':'g187323','Hamburg':'g187331','München':'g187309','Köln':'g187371',
  'Frankfurt':'g187337','Stuttgart':'g187291','Düsseldorf':'g187257','Leipzig':'g187286',
  'Dortmund':'g187373','Bremen':'g187339','Dresden':'g187255','Hannover':'g187338',
  'Nürnberg':'g187296','Augsburg':'g187267','Mannheim':'g187293','Karlsruhe':'g187276',
  'Wiesbaden':'g187343','Münster':'g187375','Freiburg':'g187272','Lübeck':'g187335',
  'Mainz':'g187344','Erfurt':'g187390','Kassel':'g187342','Heidelberg':'g187275',
  'Saarbrücken':'g187298','Regensburg':'g187297','Würzburg':'g187305','Ulm':'g187302',
  'Erlangen':'g187271','Weimar':'g187304','Jena':'g187285','Göttingen':'g187283','Bonn':'g187340',
  'Bamberg':'g187264','Bayreuth':'g187265','Passau':'g187299','Ingolstadt':'g187281',
  'Aachen':'g187228','Trier':'g187366','Koblenz':'g187376','Heidelberg':'g187275',
};
const taUrl = (name, cat='attractions') => {
  const shortName = name.replace(/ (Hbf|Bahnhof|Hauptbahnhof).*/, '').trim();
  const id = Object.entries(TA).find(([k]) => shortName.toLowerCase().includes(k.toLowerCase()))?.[1];
  if (!id) return `https://www.tripadvisor.com/Search?q=${encodeURIComponent(shortName)}+Germany`;
  return `https://www.tripadvisor.com/${cat === 'restaurants' ? 'Restaurants' : 'Attractions'}-${id}.html`;
};

// ─── Bilingual copy ─────────────────────────────────────────────────────────
const T = {
  en: {
    eyebrow: 'Deutschland-Ticket',
    title: ['The journey', 'in between', 'counts.'],
    sub: 'Discover where your regional ticket actually takes you.',
    modes: [
      { id:'soweit',       label:'So weit',       desc:'How far can you go?',         icon:'→' },
      { id:'zuzweit',      label:'Zu zweit',     desc:'Find a meeting point',        icon:'↔' },
      { id:'tagesausflug', label:'Tagesausflug', desc:'Day trips from your station', icon:'☀' },
      { id:'entlang',      label:'Entlang',       desc:'Stops along your route',      icon:'◎', soon:true },
    ],
    selectMode: 'Choose your adventure',
    continue: 'Continue',
    startingFrom:'Starting from', andFrom:'And from', from:'From', to:'To',
    explore:'Explore', findMidpoint:'Find meeting points', showRoute:'Show route',
    selectHint:'Pick a station from the list (or press Enter) to continue',
    maxTime:'Max travel time', maxChanges:'Max changes', directOnly:'Direct only',
    placeholder:'e.g. München Hbf', placeholderB:'City B',
    placeholderOrigin:'Origin', placeholderDest:'Destination',
    savedTrips:'Saved trips', back:'Back', platform:'Platform', stops:'stops',
    allStops:'All stops', detourIdeas:'Detour ideas',
    detourHint:'Get off at an intermediate stop, explore, then continue.',
    saveTrip:'Save this trip', saved:'Saved',
    noTrains:'No regional departures found.',
    noTrainsSub:'Try München Hbf or Hamburg Hbf.',
    categories:['All','Lakes','Mountains','Castles','Cities','Nature'],
    exploreTag:'Explore',
    reachableTitle: n=>`Reachable from ${n}`,
    reachableCount: n=>`${n} stations reachable`,
    direct:'Direct', hop1:'+1 change', hop2:'+2 changes',
    loadingDirect:'Loading direct connections…',
    loadingHops: n=>`Loading +${n} hop connections…`,
    reSection:'Regional Express', rbSection:'Regional Bahn', sSection:'S-Bahn',
    intermediates:'Intermediate stops',
    showNextHour:'Next hour',
    departuresTitle:'Departures',
    meetingPoints:'Meeting points',
    arrivesAt:'Arrives ~',
    rbCollapsed: n=>`${n} RB trains`,
    sCollapsed:  n=>`${n} S-Bahn trains`,
    thingsToDo:'Things to do',
    restaurants:'Restaurants',
    openTripadvisor:'Open on TripAdvisor',
    addStop:'+ Add another person',
    menu:{ title:'Menu', tagesausflug:'Tagesausflug', zuzweit:'Zu zweit', soweit:'So weit wie möglich', entlang:'Entlang der Strecke', about:'About', kofi:'Buy me a chai ☕' },
    about:{ title:'About Zwischen', body:'Zwischen means "in between" — your Deutschland-Ticket covers RE, RB and S-Bahn. This app shows what\'s reachable, finds a meeting point, or reveals how far you can go.\n\nBuilt with ♥ by Aditya Aserkar.' },
    noResults:'No meeting points found. Try stations that share a rail corridor.',
    hoursAheadBanner: h => `No trains right now — showing next available results (in ~${h}h)`,
  },
  de: {
    eyebrow: 'Deutschland-Ticket',
    title: ['Die Reise', 'dazwischen', 'zählt.'],
    sub: 'Entdecke, wohin dein Regionalticket dich wirklich führt.',
    modes: [
      { id:'soweit',       label:'So weit',       desc:'Wie weit kommst du?',            icon:'→' },
      { id:'zuzweit',      label:'Zu zweit',     desc:'Treffpunkt finden',              icon:'↔' },
      { id:'tagesausflug', label:'Tagesausflug', desc:'Tagestouren von deinem Bahnhof', icon:'☀' },
      { id:'entlang',      label:'Entlang',       desc:'Halte entlang deiner Route',     icon:'◎', soon:true },
    ],
    selectMode: 'Wähle dein Abenteuer',
    continue: 'Weiter',
    startingFrom:'Abfahrt von', andFrom:'Und von', from:'Von', to:'Nach',
    explore:'Erkunden', findMidpoint:'Treffpunkte finden', showRoute:'Route anzeigen',
    selectHint:'Wähle eine Station aus der Liste (oder drücke Enter), um fortzufahren',
    maxTime:'Maximale Reisezeit', maxChanges:'Max. Umstiege', directOnly:'Nur direkt',
    placeholder:'z.B. München Hbf', placeholderB:'Stadt B',
    placeholderOrigin:'Startbahnhof', placeholderDest:'Zielbahnhof',
    savedTrips:'Gespeicherte Reisen', back:'Zurück', platform:'Gleis', stops:'Halte',
    allStops:'Alle Halte', detourIdeas:'Umweg-Ideen',
    detourHint:'Steig aus, erkundige dich, dann weiter mit dem nächsten Zug.',
    saveTrip:'Reise speichern', saved:'Gespeichert',
    noTrains:'Keine Regionalzüge gefunden.',
    noTrainsSub:'Versuche München Hbf oder Hamburg Hbf.',
    categories:['Alle','Seen','Berge','Schlösser','Städte','Natur'],
    exploreTag:'Erkunden',
    reachableTitle: n=>`Erreichbar von ${n}`,
    reachableCount: n=>`${n} Bahnhöfe erreichbar`,
    direct:'Direkt', hop1:'+1 Umstieg', hop2:'+2 Umstiege',
    loadingDirect:'Lade direkte Verbindungen…',
    loadingHops: n=>`Lade +${n} Umstieg…`,
    reSection:'Regional Express', rbSection:'Regional Bahn', sSection:'S-Bahn',
    intermediates:'Zwischenhalte',
    showNextHour:'Nächste Stunde',
    departuresTitle:'Abfahrten',
    meetingPoints:'Treffpunkte',
    arrivesAt:'Ankunft ~',
    rbCollapsed: n=>`${n} RB-Züge`,
    sCollapsed:  n=>`${n} S-Bahn-Züge`,
    thingsToDo:'Sehenswürdigkeiten',
    restaurants:'Restaurants',
    openTripadvisor:'Auf TripAdvisor öffnen',
    addStop:'+ Person hinzufügen',
    menu:{ title:'Menü', tagesausflug:'Tagesausflug', zuzweit:'Zu zweit', soweit:'So weit wie möglich', entlang:'Entlang der Strecke', about:'Über', kofi:'Kauf mir einen Chai ☕' },
    about:{ title:'Über Zwischen', body:'Zwischen — der Moment dazwischen. Mit deinem Deutschland-Ticket: RE, RB, S-Bahn. Diese App zeigt, was erreichbar ist.\n\nMit ♥ gebaut von Aditya Aserkar.' },
    noResults:'Keine Treffpunkte gefunden. Versuche Bahnhöfe auf einem gemeinsamen Korridor.',
    hoursAheadBanner: h => `Gerade keine Züge — zeige nächste verfügbare Verbindungen (in ~${h}h)`,
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const normalise = q => {
  const m = {
    'munich':'München Hbf','muenchen':'München Hbf','nuremberg':'Nürnberg Hbf',
    'nuernberg':'Nürnberg Hbf','nurnberg':'Nürnberg Hbf','cologne':'Köln Hbf',
    'koeln':'Köln Hbf','koln':'Köln Hbf','frankfurt':'Frankfurt(Main)Hbf',
    'hanover':'Hannover Hbf','hannover':'Hannover Hbf','dusseldorf':'Düsseldorf Hbf',
    'duesseldorf':'Düsseldorf Hbf','wurzburg':'Würzburg Hbf','wuerzburg':'Würzburg Hbf',
    'saarbrucken':'Saarbrücken Hbf','saarbruecken':'Saarbrücken Hbf',
    'goettingen':'Göttingen','gottingen':'Göttingen','tubingen':'Tübingen Hbf',
    'tuebingen':'Tübingen Hbf','luebeck':'Lübeck Hbf','lubeck':'Lübeck Hbf',
    'munster':'Münster(Westf)Hbf','muenster':'Münster(Westf)Hbf',
    'freiburg':'Freiburg(Breisgau)Hbf','erlangen':'Erlangen',
  };
  return m[q.trim().toLowerCase()] || q.trim();
};

const fmtTime = pt => { if(!pt) return'??:??'; const s=pt.toString(); return`${s.slice(6,8)}:${s.slice(8,10)}`; };
const CAT_PRI = { RE:0, RB:1, S:2, ARV:3 };
// One card per destination: the timetable board lists every departure, so the
// same terminus showed up 2-3× (RE14 13:02, RE14 14:03, RE20 14:03 → Nürnberg).
// Keep the first (earliest, category-sorted) train per destination and count
// the rest so the UI can show "+N later".
const dedupeByDestination = trains => {
  const seen = new Map();
  const out = [];
  for (const t of trains) {
    const k = t.finalDestination;
    if (seen.has(k)) { seen.get(k).laterCount = (seen.get(k).laterCount||0) + 1; continue; }
    const entry = { ...t, laterCount: 0 };
    seen.set(k, entry);
    out.push(entry);
  }
  return out;
};

// ─── Motion variants ─────────────────────────────────────────────────────────
const stagger = { hidden:{}, show:{ transition:{ staggerChildren:0.07 } } };
const fadeUp  = { hidden:{opacity:0,y:20}, show:{opacity:1,y:0,transition:{type:'spring',stiffness:280,damping:26}} };
const slideUp = { hidden:{opacity:0,y:40}, show:{opacity:1,y:0,transition:{type:'spring',stiffness:300,damping:30}} };

// ─── Map helpers ─────────────────────────────────────────────────────────────
// ─── Geocoding via backend proxy (avoids Nominatim CORS + rate limits) ──────
const GEO = {};
const geocodeOne = async name => {
  if (GEO[name]) return GEO[name];
  const res = await geocodeBatch([name]);
  if (res[name]) { GEO[name] = res[name]; return res[name]; }
  return null;
};
const isInGermany = ([lat,lon]) => lat>47.3&&lat<55.1&&lon>5.9&&lon<15.1;
const geocodeBatch = async names => {
  const list = [...new Set((names||[]).filter(Boolean))];
  if (!list.length) return {};
  try {
    const r = await apiFetch(`${API_BASE}/geocode?names=${list.map(encodeURIComponent).join(',')}`);
    return await r.json();
  } catch {
    return {};
  }
};

const mkDot = (color,size=12) => L.divIcon({
  className:'',
  html:`<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
  iconSize:[size,size], iconAnchor:[size/2,size/2],
});
const mkMeetDot = (n,active) => L.divIcon({
  className:'',
  html:`<div style="width:${active?30:22}px;height:${active?30:22}px;border-radius:50%;background:#15803D;border:${active?4:2.5}px solid white;box-shadow:0 3px 10px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:${active?12:10}px;font-weight:700;color:white;font-family:sans-serif">${n}</div>`,
  iconSize:active?[30,30]:[22,22], iconAnchor:active?[15,15]:[11,11],
});

function MapFly({to}){ const map=useMap(); useEffect(()=>{if(to)map.flyTo(to.c,to.z,{animate:true,duration:.9});},[to,map]); return null; }
function MapFit({bounds}){ const map=useMap(); useEffect(()=>{if(bounds)map.fitBounds(bounds,{padding:[60,40],animate:true});},[bounds,map]); return null; }

// ─── Small components ────────────────────────────────────────────────────────
const LineBadge = ({line,category}) => {
  const cls = category==='RE'?'re':category==='RB'?'rb':category==='S'?'s':'other';
  return <span className={`badge ${cls}`}>{line}</span>;
};

function useStationSearch(query) {
  const [results,setResults]=useState([]);
  const [loading,setLoading]=useState(false);
  const tmr=useRef(null);
  // Pre-fill from landing page
  useEffect(()=>{
    const _ld=(()=>{try{const s=sessionStorage.getItem('zwischen-landing-station');if(s)return JSON.parse(s);}catch{}const h=window.location.hash.slice(1);if(h){const p=Object.fromEntries(h.split('&').map(kv=>kv.split('=')));if(p.station)return{name:decodeURIComponent(p.station),mode:p.mode||'soweit'};}return null;})();
    if(!_ld||!_ld.name)return;
    // Search for the station to get its EVA number
    apiFetch(`${API_BASE}/stations?q=${encodeURIComponent(_ld.name)}`)
      .then(r=>r.json())
      .then(d=>{
        const s=d.stations?.[0];
        if(s){
          setStep(1); // go to map input step
          // Store found station for StepMap to pick up
          sessionStorage.setItem('zwischen-prefill',JSON.stringify({station:s,mode:_ld.mode||'soweit'}));
        }
      }).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(query.length<2){setResults([]);return;}
    clearTimeout(tmr.current);
    tmr.current=setTimeout(async()=>{
      setLoading(true);
      try{
        const norm=normalise(query);
        // Fetch base query + Hbf variant in parallel
        const [r1,r2]=await Promise.all([
          apiFetch(`${API_BASE}/stations?q=${encodeURIComponent(norm)}`).then(r=>r.json()).catch(()=>({stations:[]})),
          norm.toLowerCase().includes('hbf')?Promise.resolve({stations:[]}):
            apiFetch(`${API_BASE}/stations?q=${encodeURIComponent(norm+' Hbf')}`).then(r=>r.json()).catch(()=>({stations:[]})),
        ]);
        const base=r1.stations||[];
        const hbf=r2.stations||[];
        // Merge: keep up to 2 base results + 1 Hbf result if not already present
        const seen=new Set();
        const merged=[];
        for(const s of [...base,...hbf]){
          if(merged.length>=3)break;
          if(!seen.has(s.name)){seen.add(s.name);merged.push(s);}
        }
        // Always ensure an Hbf option is in the list if not already
        const hasHbf=merged.some(s=>s.name.toLowerCase().includes('hbf'));
        if(!hasHbf&&hbf.length>0&&merged.length<3){
          const extra=hbf.find(s=>!seen.has(s.name));
          if(extra)merged.push(extra);
        }
        setResults(merged.slice(0,3));
      }
      catch{setResults([]);}
      setLoading(false);
    },280);
    return()=>clearTimeout(tmr.current);
  },[query]);
  return{results,loading};
}

function StationInput({value,onChange,onSelect,placeholder,color='#D4521A',autoFocus=false,emptyText='No station found — try the full name'}){
  const [focused,setFocused]=useState(false);
  const {results,loading}=useStationSearch(value);
  const ref=useRef(null);
  const [dropPos,setDropPos]=useState({top:0,left:0,width:0});
  const [hi,setHi]=useState(0); // keyboard-highlighted suggestion
  useEffect(()=>{setHi(0);},[results]);
  // Dropdown always reacts once 2+ chars are typed: suggestions, a "searching…"
  // row while loading, or a no-results row — never dead air.
  const show=focused&&value.length>=2;

  // Close on outside interaction
  useEffect(()=>{
    const h=e=>{if(ref.current&&!ref.current.contains(e.target))setFocused(false);};
    document.addEventListener('mousedown',h);
    document.addEventListener('touchstart',h,{passive:true});
    return()=>{document.removeEventListener('mousedown',h);document.removeEventListener('touchstart',h);};
  },[]);

  // Recalculate position using fixed viewport coords (works on mobile)
  useEffect(()=>{
    if(!ref.current)return;
    const update=()=>{
      const r=ref.current.getBoundingClientRect();
      setDropPos({top:r.bottom+4,left:r.left,width:r.width});
    };
    update();
    window.addEventListener('scroll',update,{passive:true});
    window.addEventListener('resize',update,{passive:true});
    return()=>{window.removeEventListener('scroll',update);window.removeEventListener('resize',update);};
  },[show,value]);

  const handleSelect=(s)=>{
    onSelect(s);
    setFocused(false);
    onChange(s.name);
  };

  return(
    <div className="si-wrap" ref={ref}>
      <div className="si-inner">
        <div className="si-dot" style={{background:color}}/>
        <input type="search" className="si-input" value={value} autoFocus={autoFocus}
          onChange={e=>{onChange(e.target.value);setFocused(true);}}
          onFocus={()=>setFocused(true)} placeholder={placeholder}
          role="combobox" aria-expanded={show} aria-autocomplete="list"
          aria-label={placeholder||'Station'} enterKeyHint="go"
          onKeyDown={e=>{
            // Enter (incl. mobile keyboard "Go") selects the highlighted — by
            // default the top-ranked — suggestion, so typing a full station
            // name + Enter just works instead of dead-ending on a disabled CTA.
            if(e.key==='Enter'){
              if(results.length){e.preventDefault();handleSelect(results[Math.min(hi,results.length-1)]);}
            } else if(e.key==='ArrowDown'){e.preventDefault();setHi(h=>Math.min(h+1,results.length-1));}
            else if(e.key==='ArrowUp'){e.preventDefault();setHi(h=>Math.max(h-1,0));}
            else if(e.key==='Escape'){setFocused(false);}
          }}
          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"/>
        {loading&&<div className="si-spin" style={{borderTopColor:color}}/>}
      </div>
      {show&&createPortal(
        <div style={{
          position:'fixed',top:dropPos.top,left:dropPos.left,width:Math.max(dropPos.width,240),
          zIndex:999999,background:'var(--surface)',border:'.5px solid var(--bdr2)',
          borderRadius:13,overflow:'hidden',boxShadow:'0 10px 32px rgba(0,0,0,.22)',
          maxHeight:240,overflowY:'auto'
        }}>
          {results.map((s,i)=>(
            <button key={i}
              style={{
                display:'flex',alignItems:'center',gap:8,width:'100%',
                padding:'13px 16px',border:'none',borderBottom:'.5px solid var(--bdr)',
                background:i===hi?'var(--surf3)':'none',cursor:'pointer',fontFamily:'DM Sans,sans-serif',
                fontSize:14,color:'var(--ink)',textAlign:'left',
                WebkitTapHighlightColor:'transparent',touchAction:'manipulation',
                minHeight:48, // large enough tap target on mobile
              }}
              onPointerDown={e=>{
                e.preventDefault();
                e.stopPropagation();
                handleSelect(s);
              }}>
              <div style={{width:6,height:6,borderRadius:'50%',background:color,flexShrink:0}}/>
              <span>{s.name}</span>
            </button>
          ))}
          {results.length===0&&(
            <div style={{
              display:'flex',alignItems:'center',gap:8,padding:'13px 16px',
              fontFamily:'DM Sans,sans-serif',fontSize:13,color:'var(--faint)',minHeight:48,
            }}>
              {loading
                ? <><div className="si-spin" style={{borderTopColor:color,flexShrink:0}}/>Searching…</>
                : emptyText}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Hamburger menu ───────────────────────────────────────────────────────────
function HamburgerMenu({open,onClose,lang,setLang,dark,setDark,onMode,t,savedTrips,onSavedOpen}){
  return(
    <AnimatePresence>
      {open&&(
        <>
          <motion.div className="menu-backdrop" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose}/>
          <motion.div className="menu-drawer"
            initial={{x:'100%'}} animate={{x:0}} exit={{x:'100%'}}
            transition={{type:'spring',stiffness:320,damping:32}}>
            <div className="menu-header">
              <span className="menu-title">{t.menu.title}</span>
              <button className="menu-close" onClick={onClose}>✕</button>
            </div>
            <div className="menu-section">
              {t.modes.map(m=>(
                <button key={m.id} className="menu-item" onClick={()=>{onMode(m.id);onClose();}}>
                  <span className="menu-item-icon">{m.icon}</span>
                  <span>{m.label}</span>
                </button>
              ))}
            </div>
            <div className="menu-divider"/>
            <div className="menu-section">
              <div className="menu-toggle-row">
                <span>Language</span>
                <button className="menu-toggle-btn" onClick={()=>setLang(l=>l==='en'?'de':'en')}>{lang==='en'?'Deutsch':'English'}</button>
              </div>
              <div className="menu-toggle-row">
                <span>Theme</span>
                <button className="menu-toggle-btn" onClick={()=>setDark(d=>!d)}>{dark?'Light':'Dark'}</button>
              </div>
            </div>
            <div className="menu-divider"/>
            <div className="menu-section">
              {savedTrips?.length>0&&(
                <button className="menu-item" onClick={()=>{onSavedOpen?.();onClose();}}>
                  <span className="menu-item-icon">♥</span>
                  <span>Saved trips <span style={{background:'var(--accent)',color:'#fff',borderRadius:100,padding:'1px 7px',fontSize:10,fontWeight:600,marginLeft:4}}>{savedTrips.length}</span></span>
                </button>
              )}
              <button className="menu-item" onClick={()=>{onMode('about');onClose();}}>
                <span className="menu-item-icon">ℹ</span><span>{t.menu.about}</span>
              </button>
              <a className="menu-item" href="https://ko-fi.com/adityaaserkar" target="_blank" rel="noreferrer">
                <span className="menu-item-icon">☕</span><span>{t.menu.kofi}</span>
              </a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function AboutModal({open,onClose,t}){
  return(
    <AnimatePresence>
      {open&&(
        <>
          <motion.div className="menu-backdrop" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose}/>
          <motion.div className="about-modal"
            initial={{opacity:0,y:40,scale:.97}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:20,scale:.97}}
            transition={{type:'spring',stiffness:300,damping:28}}>
            <div className="menu-header">
              <span className="menu-title">{t.about.title}</span>
              <button className="menu-close" onClick={onClose}>✕</button>
            </div>
            <div className="about-body">
              {t.about.body.split('\n\n').map((p,i)=><p key={i}>{p}</p>)}
            </div>
            <a className="kofi-btn" href="https://ko-fi.com/adityaaserkar" target="_blank" rel="noreferrer">☕ Buy me a chai</a>
            <div style={{textAlign:"center",padding:"8px 0 4px",display:"flex",gap:14,justifyContent:"center"}}>
              <a href="/privacy.html" style={{fontSize:11,color:"var(--faint)",textDecoration:"none"}} target="_blank" rel="noreferrer">Privacy Policy</a>
              <a href="/impressum.html" style={{fontSize:11,color:"var(--faint)",textDecoration:"none"}} target="_blank" rel="noreferrer">Impressum</a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── Step-progress indicator ──────────────────────────────────────────────────
function StepDots({step}){
  return(
    <div className="step-dots">
      {[0,1,2].map(i=>(
        <motion.div key={i} className={`step-dot ${i===step?'active':i<step?'done':''}`}
          layoutId={i===step?'step-active':undefined}
          animate={{ scale: i===step?1.2:1, opacity: i>step?0.3:1 }}
          transition={{type:'spring',stiffness:400,damping:28}}/>
      ))}
    </div>
  );
}

// ─── Reachability panel ───────────────────────────────────────────────────────
function ReachablePanel({evaNo,stationName,t}){
  const [hops,setHops]=useState(0);
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(false);
  const [rbOpen,setRbOpen]=useState(false);
  const [showSbahn,setShowSbahn]=useState(false); // hidden by default

  const load=useCallback(async h=>{
    setLoading(true);
    try{const r=await apiFetch(`${API_BASE}/reachable?eva=${evaNo}&hops=${h}`);const d=await r.json();setData(d);}
    catch{}
    setLoading(false);
  },[evaNo]);

  useEffect(()=>{load(0);},[evaNo]);

  return(
    <div className="reach-panel">
      <div className="reach-header">
        <div>
          <div className="reach-title">{t.reachableTitle(stationName)}</div>
          {data&&<div className="reach-count">{t.reachableCount(data.totalUnique)}</div>}
        </div>
        <div className="hop-tabs">
          {[0,1,2].map(h=>(
            <button key={h} className={`hop-tab ${hops===h?'active':''}`}
              onClick={()=>{setHops(h);load(h);}} disabled={loading}>
              {h===0?t.direct:h===1?'+1':'+2'}
            </button>
          ))}
        </div>
      </div>
      {loading&&<div className="loading-row"><div className="dots"><span/><span/><span/></div><span className="loading-txt">{t.loadingDirect}</span></div>}
      {!loading&&data&&(()=>{
        const trains=data.byHop[0]?.trains||[];
        const reTr=trains.filter(t=>t.category==='RE');
        const rbTr=trains.filter(t=>t.category==='RB');
        const sTr=trains.filter(t=>['S','ARV'].includes(t.category));
        const seen=new Set();
        const uniq=arr=>arr.filter(s=>seen.has(s)?false:seen.add(s)||true);
        const reFin=uniq([...new Set(reTr.map(t=>t.finalDestination))]);
        const rbFin=uniq([...new Set(rbTr.map(t=>t.finalDestination))]);
        const sFin=uniq([...new Set(sTr.map(t=>t.finalDestination))]);
        return(
          <div className="reach-body">
            {reTr.length>0&&(
              <div className="reach-group">
                <div className="reach-group-hd"><span className="badge re">{t.reSection}</span></div>
                <div className="train-rows">{reTr.map((tr,i)=><div key={i} className="train-row-sm"><span className="badge re">{tr.line}</span><span className="tdep">{tr.departure}</span><span className="tdest">→ {tr.finalDestination}</span></div>)}</div>
                <div className="chips">{reFin.map((s,i)=><span key={i} className="chip chip-re">{s}</span>)}</div>
              </div>
            )}
            {rbTr.length>0&&(
              <div className="reach-group">
                <button className="collapse-toggle" onClick={()=>setRbOpen(o=>!o)}>
                  <span>{t.rbCollapsed(rbTr.length)}</span><span className="carrow">{rbOpen?'▲':'▼'}</span>
                </button>
                <AnimatePresence>
                  {rbOpen&&<motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} style={{overflow:'hidden'}}>
                    <div className="train-rows" style={{marginTop:6}}>{rbTr.map((tr,i)=><div key={i} className="train-row-sm"><span className="badge rb">{tr.line}</span><span className="tdep">{tr.departure}</span><span className="tdest">→ {tr.finalDestination}</span></div>)}</div>
                    <div className="chips">{rbFin.map((s,i)=><span key={i} className="chip chip-rb">{s}</span>)}</div>
                  </motion.div>}
                </AnimatePresence>
              </div>
            )}
            {/* S-Bahn behind checkbox */}
            {sTr.length>0&&(
              <div className="reach-group">
                <label className="sbahn-toggle">
                  <input type="checkbox" checked={showSbahn} onChange={e=>setShowSbahn(e.target.checked)}/>
                  <span>{t.sCollapsed(sTr.length)}</span>
                </label>
                <AnimatePresence>
                  {showSbahn&&<motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} style={{overflow:'hidden'}}>
                    <div className="train-rows" style={{marginTop:6}}>{sTr.map((tr,i)=><div key={i} className="train-row-sm"><span className="badge s">{tr.line}</span><span className="tdep">{tr.departure}</span><span className="tdest">→ {tr.finalDestination}</span></div>)}</div>
                    <div className="chips">{sFin.map((s,i)=><span key={i} className="chip chip-s">{s}</span>)}</div>
                  </motion.div>}
                </AnimatePresence>
              </div>
            )}
            {hops>=1&&data.byHop[1]&&(
              <div className="reach-extra">
                <div className="reach-section-lbl"><span className="hop-badge-sm bdg-hop1">{t.hop1}</span><span>{data.byHop[1].stations.length} additional</span></div>
                <div className="chips">{data.byHop[1].stations.slice(0,50).map((s,i)=><span key={i} className="chip chip-hop1">{s}</span>)}</div>
              </div>
            )}
            {hops>=2&&data.byHop[2]&&(
              <div className="reach-extra">
                <div className="reach-section-lbl"><span className="hop-badge-sm bdg-hop2">{t.hop2}</span><span>{data.byHop[2].stations.length} additional</span></div>
                <div className="chips">{data.byHop[2].stations.slice(0,50).map((s,i)=><span key={i} className="chip chip-hop2">{s}</span>)}</div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── STEP 3: RESULTS ─────────────────────────────────────────────────────────
// Full-screen map background. Cards float on top. MP detail slides up as sheet.
// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// ─── MP DETAIL PAGE (slides in from carousel card tap) ───────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function MpDetail({mp,data,dark,t,savedTrips,onSaveToggle,onBack}){
  const [showStops,setShowStops]=useState(false);
  const isSaved=id=>savedTrips.some(s=>s.id===id);
  const geo=data.mpGeos?.[mp.station];
  const shortName=mp.station.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').trim();
  const spread=mp.spread;
  const spreadClass=spread!=null&&spread<=10?'dot-green':spread!=null&&spread<=25?'dot-amber':'dot-grey';
  const spreadLabel=spread!=null?(spread<=5?'Arrive together':`Within ${spread} min`):'Meeting point';

  return(
    <div className="results-fullscreen">
      {/* Map background — zoomed into meeting point */}
      <div className="results-map-bg">
        <MapContainer center={geo||[51.2,10.5]} zoom={geo?11:6}
          style={{width:'100%',height:'100%'}} zoomControl={false} attributionControl={false}>
          <TileLayer url={dark?`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`} subdomains="abcd" maxZoom={20}/>
          <TileLayer url={dark?`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`} subdomains="abcd" maxZoom={20} pane="shadowPane"/>
          {/* Origin markers */}
          {data.persons?.map((p,i)=>p.geo&&(
            <Marker key={i} position={p.geo} icon={mkDot(PERSON_COLORS[i],13)}>
              <Popup>{p.station?.name}</Popup>
            </Marker>
          ))}
          {/* Lines to this MP */}
          {data.persons?.map((p,i)=>{
            const pGeo = p.geo || data.oGeos?.[p.station?.name];
            if(!pGeo||!geo)return null;
            return(
              <Polyline key={i} positions={[pGeo,geo]}
                pathOptions={{color:PERSON_COLORS[i],weight:3,opacity:.7,dashArray:'8 6'}}/>
            );
          })}
          {/* Meeting point pin */}
          {geo&&<Marker position={geo} icon={mkMeetDot(1,true)}><Popup>{mp.station}</Popup></Marker>}
        </MapContainer>
      </div>

      {/* Back pill — floating on map, top-left */}
      <button className="float-back-pill" onClick={onBack}>← {t.back}</button>

      {/* Inset detail card */}
      <motion.div className="inset-card"
        initial={{y:'100%'}} animate={{y:0}}
        transition={{type:'spring',stiffness:280,damping:32}}>
        <div className="sheet-handle-row"><div className="sheet-handle"/></div>
        <div className="rc-scroll">
          {/* Title */}
          <h2 className="rc-title">{shortName.toUpperCase()}.</h2>
          <div className="rc-meta-row">
            <span className={`rc-status-dot ${spreadClass}`}/>
            <span className="rc-meta-text">{spreadLabel}</span>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-text">{mp.personDetails?.length} people</span>
          </div>

          <div className="rc-divider"/>

          {/* Journey cards — one per person, expanded */}
          {mp.personDetails?.map((pd,pi)=>{
            const col=PERSON_COLORS[pi]||PERSON_COLORS[0];
            const train=pd.trains?.[0];
            return(
              <div key={pi} className="journey-card" style={{borderColor:col+'33'}}>
                <div className="journey-card-header">
                  <div className="rc-person-icon" style={{background:col+'22',color:col}}>
                    {String.fromCharCode(65+pi)}
                  </div>
                  <div style={{flex:1}}>
                    <div className="rc-action-label">{pd.stationName}</div>
                    <div style={{fontSize:10,color:'#999'}}>Departing {pd.trains?.[0]?.departure||'—'}</div>
                  </div>
                  {pd.arrivalTime&&(
                    <div style={{textAlign:'right'}}>
                      <div style={{fontFamily:"'Fraunces',serif",fontSize:15,fontWeight:700,color:'#111'}}>~{pd.arrivalTime}</div>
                      <div style={{fontSize:10,color:'#999'}}>arrives</div>
                    </div>
                  )}
                </div>
                {train&&(
                  <div className="journey-train-row">
                    <LineBadge line={train.line} category={train.category}/>
                    <span style={{fontSize:11,color:'#555',flex:1,marginLeft:6}}>
                      {pd.stationName.replace(/ (Hbf|Hauptbahnhof).*/,'')} → {shortName}
                    </span>
                    <span style={{fontFamily:"'Fraunces',serif",fontSize:12,fontWeight:600,color:col}}>{train.departure}</span>
                  </div>
                )}
              </div>
            );
          })}

          <div className="rc-divider"/>

          {/* Compact action tiles */}
          <div className="action-tiles">
            <a className="action-tile" href={taUrl(mp.station,'attractions')} target="_blank" rel="noreferrer">
              <span className="at-icon">🗺</span><span className="at-lbl">Explore</span>
            </a>
            <a className="action-tile" href={taUrl(mp.station,'restaurants')} target="_blank" rel="noreferrer">
              <span className="at-icon">🍽</span><span className="at-lbl">Eat</span>
            </a>
            <button className="action-tile" onClick={()=>setShowStops(s=>!s)}>
              <span className="at-icon">🚆</span>
              <span className="at-lbl">{showStops?'Hide':'Stops'}</span>
            </button>
          </div>

          {/* Stops expanded per person */}
          <AnimatePresence>
            {showStops&&(
              <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} style={{overflow:'hidden'}}>
                {mp.personDetails?.map((pd,pi)=>{
                  const col=PERSON_COLORS[pi]||PERSON_COLORS[0];
                  const train=pd.trains?.[0];
                  if(!train)return null;
                  return(
                    <div key={pi} style={{marginTop:12}}>
                      <div className="rc-stops-label" style={{color:col}}>
                        {String.fromCharCode(65+pi)} · {train.line} from {pd.stationName.replace(/ (Hbf|Hauptbahnhof).*/,'')}
                      </div>
                      <div className="rc-stops-list">
                        <div className="rc-stop rc-stop-origin">
                          <div className="rc-stop-dot" style={{background:col,border:`1.5px solid ${col}`}}/>
                          <span>{pd.stationName}</span>
                          <span className="rc-stop-time" style={{color:col}}>{train.departure}</span>
                        </div>
                        <div className="rc-stop rc-stop-origin">
                          <div className="rc-stop-dot" style={{background:'#22c55e',border:'1.5px solid #22c55e'}}/>
                          <span>{mp.station}</span>
                          <span className="rc-stop-time" style={{color:'#22c55e'}}>~{pd.arrivalTime}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Save button */}
        <div className="inset-card-footer">
          <button className="footer-btn footer-btn-accent" style={{flex:'unset',width:'100%'}}
            onClick={()=>onSaveToggle({id:mp.station,station:mp.station,line:'',category:''},null)}>
            ♡ Save trip
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── STEP 3: RESULTS ─────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function StepResults({data,dark,t,savedTrips,onSaveToggle,onBack,onHome}){
  const {mode}=data;
  const isZuzweit=mode==='zuzweit';
  const isSoweit=mode==='soweit'||mode==='tagesausflug';
  const [resMenuOpen,setResMenuOpen]=useState(false);
  const [resShowSaved,setResShowSaved]=useState(false);

  // Shared carousel state
  const [activeIdx,setActiveIdx]=useState(0);
  const [mapFly,setMapFly]=useState(null);
  const [mapFit,setMapFit]=useState(data.mapFit||null);
  const [showDetail,setShowDetail]=useState(null); // MP detail page
  const [detailTrain,setDetailTrain]=useState(null); // single train detail
  const [showStops,setShowStops]=useState(false);
  const dragStartX=useRef(null);

  // Single-origin trains
  const [trains,setTrains]=useState([]);
  const [loadingTrains,setLoadingTrains]=useState(false);
  const [hourOffset,setHourOffset]=useState(0);
  const [category,setCategory]=useState(0);
  const [activeHops,setActiveHops]=useState(data.hops||0);

  const loadTrains=useCallback(async(station,offset=0,hopsOverride=null)=>{
    if(hopsOverride!==null)setActiveHops(hopsOverride);
    setLoadingTrains(true);setTrains([]);
    const hopsToUse=hopsOverride??data.hops??0;
    try{
      const r=await apiFetch(`${API_BASE}/timetable?eva=${station.eva}&offset=${offset}`);
      const d=await r.json();
      const stops=d?.timetable?.s||[];
      const newTrains=stops.filter(s=>['RE','RB','S','ARV'].includes(s?.tl?.c)&&s.dp)
        .map(s=>({id:s.id,line:s.dp?.l||s.dp?.fb||`${s.tl.c} ${s.tl.n}`,category:s.tl.c,
          departure:fmtTime(s.dp.pt),platform:s.dp.pp,
          allStops:s.dp.ppth?s.dp.ppth.split('|'):[],
          finalDestination:s.dp.ppth?s.dp.ppth.split('|').pop():'Unknown'}))
        .sort((a,b)=>{const cd=(CAT_PRI[a.category]??9)-(CAT_PRI[b.category]??9);return cd!==0?cd:a.departure.localeCompare(b.departure);});
      setTrains(dedupeByDestination(newTrains));
      // Geocode direct RE destinations
      const reNames=[...new Set(newTrains.filter(t=>t.category==='RE').map(t=>t.finalDestination))].slice(0,8);
      geocodeBatch(reNames).then(geos=>setDestGeos(prev=>({...prev,...geos})));
      // If hops > 0, fetch expanded tree data
      if(hopsToUse>0){
        apiFetch(`${API_BASE}/reachable?eva=${station.eva}&hops=${hopsToUse}`)
          .then(r=>r.json()).then(d=>{
            setTreeData(d);
            // Geocode hop via stations and their destinations
            const hop1=(d.byHop[1]?.trains||[]);
            const hop2=(d.byHop[2]?.trains||[]);
            const vias=[...new Set([...hop1.map(t=>t.via),...hop2.map(t=>t.via),...hop2.filter(t=>t.via1).map(t=>t.via1)])].slice(0,12);
            const dests=[...new Set([...hop1.filter(t=>t.category==='RE').map(t=>t.finalDestination),...hop2.filter(t=>t.category==='RE').map(t=>t.finalDestination)])].slice(0,14);
            geocodeBatch([...vias,...dests]).then(g=>setHopGeos(prev=>({...prev,...g})));
          }).catch(()=>{});
      } else {
        setTreeData(null);
        setHopGeos({});
      }
    }catch{}
    setLoadingTrains(false);
  },[data.hops]);

  // Reachable stations for hop-expanded map tree
  const [reachableStations,setReachableStations]=useState([]);
  const [destGeos,setDestGeos]=useState({});

  // Tree data: hop0=direct trains, hop1/2=connection trains with via
  const [treeData,setTreeData]=useState(null);
  // Which RB/S clusters are expanded (keyed by via station name)
  const [expandedClusters,setExpandedClusters]=useState({});

  useEffect(()=>{
    if(!isZuzweit&&data.station){
      loadTrains(data.station,0);
      const hops=data.hops||0;
      if(hops>0){
        apiFetch(`${API_BASE}/reachable?eva=${data.station.eva}&hops=${hops}`)
          .then(r=>r.json())
          .then(d=>setTreeData(d))
          .catch(()=>{});
      }
    }
  },[]);

  // Geocode: RE final destinations for direct trains (max 8)
  useEffect(()=>{
    if(!isZuzweit&&trains.length>0){
      const reTrains=trains.filter(t=>t.category==='RE');
      const names=[...new Set(reTrains.map(t=>t.finalDestination))].slice(0,8);
      geocodeBatch(names).then(geos=>setDestGeos(geos));
    }
  },[trains]);

  // Geocode hop1 RE via stations + their destinations on demand
  const [hopGeos,setHopGeos]=useState({});
  useEffect(()=>{
    if(!treeData)return;
    const hop1=(treeData.byHop[1]?.trains||[]);
    const hop2=(treeData.byHop[2]?.trains||[]);
    const vias=[...new Set([
      ...hop1.map(t=>t.via),
      ...hop2.map(t=>t.via),   // hop2 change stations
      ...hop2.filter(t=>t.via1).map(t=>t.via1), // hop1 change stations (already in hop1 vias but belt+suspenders)
    ])].slice(0,12);
    const reDests=[...new Set([
      ...hop1.filter(t=>t.category==='RE').map(t=>t.finalDestination),
      ...hop2.filter(t=>t.category==='RE').map(t=>t.finalDestination),
    ])].slice(0,14);
    geocodeBatch([...vias,...reDests]).then(g=>setHopGeos(prev=>({...prev,...g})));
  },[treeData]);

  // When a cluster is expanded, geocode its destinations
  const expandCluster=(viaName,trains)=>{
    setExpandedClusters(prev=>({...prev,[viaName]:!prev[viaName]}));
    if(!expandedClusters[viaName]){
      const names=[...new Set(trains.map(t=>t.finalDestination))].slice(0,8);
      geocodeBatch(names).then(g=>setHopGeos(prev=>({...prev,...g})));
    }
  };

  const isSaved=id=>savedTrips.some(t=>t.id===id);

  // Carousel items — MP list for zuzweit, train list for soweit/tagesausflug
  const carouselItems=isZuzweit?(data.mps||[]):trains;

  const goToCard=useCallback(idx=>{
    if(idx<0||idx>=carouselItems.length)return;
    setActiveIdx(idx);
    if(isZuzweit){
      const geo=data.mpGeos?.[carouselItems[idx]?.station];
      if(geo)setMapFly({c:geo,z:12});
    } else {
      // Fly to the train destination
      const train=carouselItems[idx];
      if(train&&destGeos[train.finalDestination])
        setMapFly({c:destGeos[train.finalDestination],z:10});
    }
  },[carouselItems,isZuzweit,data.mpGeos,destGeos]);

  useEffect(()=>{
    if(isZuzweit&&carouselItems.length>0) goToCard(0);
  },[isZuzweit]);

  useEffect(()=>{
    if(!isZuzweit&&data.stationGeo){
      const all=[data.stationGeo,...Object.values(destGeos),...Object.values(hopGeos)].filter(Boolean);
      if(all.length<2)return;
      const lats=all.map(g=>g[0]),lons=all.map(g=>g[1]);
      const pad=(data.hops||0)>0?.4:.12;
      setMapFit([[Math.min(...lats)-pad,Math.min(...lons)-pad],[Math.max(...lats)+pad,Math.max(...lons)+pad]]);
    }
  },[destGeos,hopGeos]);

  const onDragStart=e=>{ dragStartX.current=e.touches?e.touches[0].clientX:e.clientX; };
  const onDragEnd=e=>{
    if(dragStartX.current===null)return;
    const endX=e.changedTouches?e.changedTouches[0].clientX:e.clientX;
    const diff=dragStartX.current-endX;
    if(Math.abs(diff)>40){diff>0?goToCard(activeIdx+1):goToCard(activeIdx-1);}
    dragStartX.current=null;
  };

  // MP detail rendered as overlay — no separate return so map stays mounted

  // ── Train detail (single-origin) ────────────────────────────────────────────
  if(detailTrain){
    const name=detailTrain.finalDestination.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').trim();
    return(
      <div className="results-fullscreen">
        <div className="results-map-bg">
          <MapContainer center={data.stationGeo||[51.2,10.5]} zoom={data.stationGeo?9:6}
            style={{width:'100%',height:'100%'}} zoomControl={false} attributionControl={false}>
            <TileLayer url={dark?`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`} subdomains="abcd" maxZoom={20}/>
            <TileLayer url={dark?`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`} subdomains="abcd" maxZoom={20} pane="shadowPane"/>
            {data.stationGeo&&<Marker position={data.stationGeo} icon={mkDot(PERSON_COLORS[0],14)}><Popup>{data.station?.name}</Popup></Marker>}
          </MapContainer>
        </div>
        <button className="float-back-pill" onClick={()=>setDetailTrain(null)}>← Back</button>
        <motion.div className="inset-card" initial={{y:'100%'}} animate={{y:0}} transition={{type:'spring',stiffness:280,damping:32}}>
          <div className="sheet-handle-row"><div className="sheet-handle"/></div>
          <div className="rc-scroll">
            <h2 className="rc-title">{name.toUpperCase()}.</h2>
            <div className="rc-meta-row">
              <span className="rc-status-dot dot-green"/>
              <span className="rc-meta-text">{detailTrain.departure}</span>
              <span className="rc-meta-sep">·</span>
              <LineBadge line={detailTrain.line} category={detailTrain.category}/>
              <span className="rc-meta-sep">·</span>
              <span className="rc-meta-text">{t.platform} {detailTrain.platform}</span>
            </div>
            <div className="rc-divider"/>
            <div className="action-tiles">
              <a className="action-tile" href={taUrl(detailTrain.finalDestination,'attractions')} target="_blank" rel="noreferrer">
                <span className="at-icon">🗺</span><span className="at-lbl">Explore</span>
              </a>
              <a className="action-tile" href={taUrl(detailTrain.finalDestination,'restaurants')} target="_blank" rel="noreferrer">
                <span className="at-icon">🍽</span><span className="at-lbl">Eat</span>
              </a>
              <button className="action-tile" onClick={()=>setShowStops(s=>!s)}>
                <span className="at-icon">🚆</span><span className="at-lbl">{showStops?'Hide':'Stops'}</span>
              </button>
            </div>
            <AnimatePresence>
              {showStops&&(
                <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} style={{overflow:'hidden'}}>
                  <div className="rc-divider"/>
                  <div className="rc-stops-label">{t.allStops} ({detailTrain.allStops.length})</div>
                  <div className="rc-stops-list">
                    <div className="rc-stop rc-stop-origin">
                      <div className="rc-stop-dot rc-stop-dot-origin"/>
                      <span>{data.station?.name}</span>
                      <span className="rc-stop-time">{detailTrain.departure}</span>
                    </div>
                    {detailTrain.allStops.map((s,i)=>(
                      <div key={i} className="rc-stop">
                        <div className="rc-stop-dot"/><span>{s}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="inset-card-footer">
            <button className="footer-btn footer-btn-accent" style={{flex:'unset',width:'100%'}} onClick={()=>onSaveToggle(detailTrain,data.station?.name)}>
              {isSaved(detailTrain.id)?`♥ ${t.saved}`:`♡ ${t.saveTrip}`}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Main results view ────────────────────────────────────────────────────────
  return(
    <div className="results-fullscreen">

      {/* Full-screen map */}
      <div className="results-map-bg">
        <MapContainer center={[51.2,10.5]} zoom={isZuzweit?6:8}
          style={{width:'100%',height:'100%'}} zoomControl={false} attributionControl={false}>
          <TileLayer
            url={dark?`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            attribution="© OpenStreetMap © CARTO" subdomains="abcd" maxZoom={20}/>
          <TileLayer
            url={dark?`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            subdomains="abcd" maxZoom={20} pane="shadowPane"/>
          <MapFly to={mapFly}/>
          <MapFit bounds={mapFit}/>

          {/* ZU ZWEIT: origin dots + dashed lines to active MP + numbered MP pins */}
          {isZuzweit&&(()=>{
            const activeMp=carouselItems[activeIdx];
            const activeMpGeo=activeMp?data.mpGeos?.[activeMp.station]:null;
            return(<>
              {/* Origin markers */}
              {data.persons?.map((p,i)=>{
                const pGeo = p.geo || data.oGeos?.[p.station?.name];
                if(!pGeo)return null;
                return(
                  <Marker key={`o${i}`} position={pGeo} icon={mkDot(PERSON_COLORS[i],14)}>
                    <Popup>{p.station?.name}</Popup>
                  </Marker>
                );
              })}
              {/* Lines from each origin to the active meeting point */}
              {activeMpGeo&&data.persons?.map((p,i)=>{
                const pGeo = p.geo || data.oGeos?.[p.station?.name];
                if(!pGeo)return null;
                return(
                  <Polyline key={`line${i}-${activeIdx}`}
                    positions={[pGeo, activeMpGeo]}
                    pathOptions={{color:PERSON_COLORS[i],weight:3,opacity:.7,dashArray:'8 6'}}/>
                );
              })}
              {/* All MP pins — active is large green, others small grey */}
              {carouselItems.map((mp,i)=>{
                const geo=data.mpGeos?.[mp.station];
                if(!geo)return null;
                return(
                  <Marker key={`mp${i}`} position={geo}
                    icon={mkMeetDot(i+1,i===activeIdx)}
                    eventHandlers={{click:()=>goToCard(i)}}>
                    <Popup>{mp.station}</Popup>
                  </Marker>
                );
              })}
            </>);
          })()}

          {/* SINGLE ORIGIN: radial tree — RE always shown, RB/S collapsed */}
          {!isZuzweit&&data.stationGeo&&(
            <>
              {/* Direct RE: solid lines */}
              {trains.filter(t=>t.category==='RE').map((train,i)=>{
                const geo=destGeos[train.finalDestination];
                if(!geo)return null;
                const idx=trains.indexOf(train);
                const isActive=idx===activeIdx;
                return(<Polyline key={`m-re-l-${train.id}`} positions={[data.stationGeo,geo]}
                  pathOptions={{color:'#1D4ED8',weight:isActive?4:2.5,opacity:isActive?.95:.65}}/>);
              })}
              {trains.filter(t=>t.category==='RE').map((train,i)=>{
                const geo=destGeos[train.finalDestination];
                if(!geo)return null;
                const idx=trains.indexOf(train);
                const isActive=idx===activeIdx;
                return(<Marker key={`m-re-d-${train.id}`} position={geo}
                  icon={mkDot('#1D4ED8',isActive?16:9)}
                  eventHandlers={{click:()=>goToCard(idx)}}>
                  <Popup>{train.finalDestination}</Popup>
                </Marker>);
              })}

              {/* RB/S cluster pill — tap to expand */}
              {(()=>{
                const rbsT=trains.filter(t=>['RB','S','ARV'].includes(t.category));
                if(!rbsT.length)return null;
                const isExp=expandedClusters['__origin__'];
                return(<>
                  <Marker position={[data.stationGeo[0]-0.025,data.stationGeo[1]+0.05]}
                    icon={L.divIcon({className:'',iconSize:[68,22],iconAnchor:[0,11],
                      html:`<div style="background:${isExp?'#7C3AED':'#f2e8f5'};color:${isExp?'#fff':'#7C3AED'};border:1.5px solid #7C3AED;border-radius:100px;padding:2px 9px;font-size:10px;font-weight:600;white-space:nowrap;font-family:'DM Sans',sans-serif;box-shadow:0 2px 8px rgba(0,0,0,.18)">${isExp?'▲ collapse':'RB / S +'+ rbsT.length}</div>`})}
                    eventHandlers={{click:()=>{
                      const next=!expandedClusters['__origin__'];
                      setExpandedClusters(prev=>({...prev,'__origin__':next}));
                      if(next){
                        const names=[...new Set(rbsT.map(t=>t.finalDestination))].slice(0,8);
                        geocodeBatch(names).then(g=>setDestGeos(prev=>({...prev,...g})));
                      }
                    }}}>
                    <Popup>Tap to show RB and S-Bahn trains</Popup>
                  </Marker>
                  {isExp&&rbsT.map((train,i)=>{
                    const geo=destGeos[train.finalDestination];
                    if(!geo)return null;
                    const col=train.category==='RB'?'#15803D':'#7C3AED';
                    const idx=trains.indexOf(train);
                    return([<Polyline key={`m-rbs-l-${train.id||i}`} positions={[data.stationGeo,geo]}
                      pathOptions={{color:col,weight:2,opacity:.6,dashArray:'6 4'}}/>,
                      <Marker key={`m-rbs-d-${train.id||i}`} position={geo} icon={mkDot(col,8)}
                        eventHandlers={{click:()=>goToCard(idx)}}>
                        <Popup>{train.finalDestination}</Popup>
                      </Marker>]);
                  })}
                </>);
              })()}

              {/* Hop 1: chain — origin→via, then via→new destinations branching */}
              {treeData&&(()=>{
                const hop1RE=(treeData.byHop[1]?.trains||[]).filter(t=>t.category==='RE');
                const directDests=new Set(trains.filter(t=>t.category==='RE').map(t=>t.finalDestination));
                const vias=[...new Set(hop1RE.map(t=>t.via))].slice(0,6);
                return vias.map((via,i)=>{
                  const vGeo=hopGeos[via];
                  if(!vGeo)return null;
                  const viaIsDirect=directDests.has(via);
                  const viaGeo=viaIsDirect?(destGeos[via]||vGeo):vGeo;
                  const viaDests=hop1RE.filter(t=>t.via===via&&!directDests.has(t.finalDestination))
                    .filter((t,j,a)=>a.findIndex(x=>x.finalDestination===t.finalDestination)===j).slice(0,5);
                  const viaNonRE=(treeData.byHop[1]?.trains||[]).filter(t=>t.via===via&&['RB','S','ARV'].includes(t.category));
                  const isExp=expandedClusters[via];
                  return(<>
                    {!viaIsDirect&&<Polyline key={`m-h1vl-${i}-${via}`} positions={[data.stationGeo,vGeo]}
                      pathOptions={{color:'#1D4ED8',weight:2,opacity:.5,dashArray:'8 5'}}/>}
                    {!viaIsDirect&&<Marker key={`m-h1vd-${i}-${via}`} position={vGeo} icon={mkDot('#1D4ED8',12)}>
                      <Popup><strong>{via}</strong><br/>Change here</Popup>
                    </Marker>}
                    {viaNonRE.length>0&&(
                      <Marker key={`m-h1cl-${i}-${via}`}
                        position={[vGeo[0]+0.015,vGeo[1]+0.03]}
                        icon={L.divIcon({className:'',iconSize:[62,22],iconAnchor:[0,11],
                          html:`<div style="background:${isExp?'#15803D':'#e8f2ec'};color:${isExp?'#fff':'#15803D'};border:1.5px solid #15803D;border-radius:100px;padding:2px 9px;font-size:10px;font-weight:600;white-space:nowrap;font-family:'DM Sans',sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.18)">${isExp?'▲':'+'+viaNonRE.length+' RB/S'}</div>`})}
                        eventHandlers={{click:()=>expandCluster(via,viaNonRE)}}>
                        <Popup>RB/S from {via}</Popup>
                      </Marker>
                    )}
                    {isExp&&viaNonRE.map((t,j)=>{
                      const dGeo=hopGeos[t.finalDestination];
                      if(!dGeo)return null;
                      const col=t.category==='RB'?'#15803D':'#7C3AED';
                      return([<Polyline key={`m-h1rbl-${i}-${j}-${t.finalDestination}`} positions={[viaGeo,dGeo]}
                        pathOptions={{color:col,weight:1.5,opacity:.55,dashArray:'5 4'}}/>,
                        <Marker key={`m-h1rbd-${i}-${j}-${t.finalDestination}`} position={dGeo} icon={mkDot(col,7)}>
                          <Popup>{t.finalDestination}</Popup>
                        </Marker>]);
                    })}
                    {viaDests.map((t,j)=>{
                      const dGeo=hopGeos[t.finalDestination];
                      if(!dGeo)return null;
                      return([<Polyline key={`m-h1rel-${i}-${j}-${t.finalDestination}`} positions={[viaGeo,dGeo]}
                        pathOptions={{color:'#1D4ED8',weight:2,opacity:.5,dashArray:'6 4'}}/>,
                        <Marker key={`m-h1red-${i}-${j}-${t.finalDestination}`} position={dGeo} icon={mkDot('#1D4ED8',9)}>
                          <Popup>{t.finalDestination}</Popup>
                        </Marker>]);
                    })}
                  </>);
                });
              })()}

              {/* Hop 2: chains — via1→via2→dest (only RE, only new destinations) */}
              {treeData&&(treeData.byHop[2]?.trains||[])
                .filter(t=>t.category==='RE')
                .filter((t,i,a)=>a.findIndex(x=>x.finalDestination===t.finalDestination&&x.via===t.via)===i)
                .slice(0,20)
                .map((t,i)=>{
                  const via2Geo=hopGeos[t.via];       // 2nd change point
                  const destGeo=hopGeos[t.finalDestination];
                  if(!via2Geo||!destGeo)return null;
                  // Check not already shown as direct or hop1
                  const directDests=new Set(trains.filter(tr=>tr.category==='RE').map(tr=>tr.finalDestination));
                  const hop1Dests=new Set((treeData.byHop[1]?.trains||[]).filter(tr=>tr.category==='RE').map(tr=>tr.finalDestination));
                  if(directDests.has(t.finalDestination)||hop1Dests.has(t.finalDestination))return null;
                  return([
                    <Polyline key={`m-h2-l-${i}-${t.finalDestination}`}
                      positions={[via2Geo,destGeo]}
                      pathOptions={{color:'#1D4ED8',weight:1.5,opacity:.35,dashArray:'4 7'}}/>,
                    <Marker key={`m-h2-d-${i}-${t.finalDestination}`} position={destGeo}
                      icon={mkDot('#1D4ED8',7)}>
                      <Popup>{t.finalDestination}</Popup>
                    </Marker>
                  ]);
                })}

              {/* Origin — always on top */}
              <Marker position={data.stationGeo} icon={mkDot('#D4521A',18)}>
                <Popup>{data.station?.name}</Popup>
              </Marker>
            </>
          )}
        </MapContainer>
      </div>

      {/* Floating nav buttons — no topbar, three independent pills */}
      <button className="float-back-pill" onClick={onBack}>← {t.back}</button>
      <div style={{position:'fixed',top:14,right:14,zIndex:500,display:'flex',gap:6,alignItems:'center'}}>
        {savedTrips.length>0&&(
          <button onClick={()=>setResShowSaved(true)}
            style={{padding:'5px 11px',borderRadius:100,background:'rgba(255,255,255,.92)',
              border:'0.5px solid rgba(0,0,0,.12)',boxShadow:'0 2px 10px rgba(0,0,0,.18)',
              backdropFilter:'blur(10px)',fontSize:11,fontWeight:500,color:'#D4521A',
              cursor:'pointer',fontFamily:"'DM Sans',sans-serif",display:'flex',alignItems:'center',gap:4}}>
            ♥ {savedTrips.length}
          </button>
        )}
        <button onClick={()=>setResMenuOpen(true)}
          style={{width:34,height:34,borderRadius:10,background:'rgba(255,255,255,.92)',
            border:'0.5px solid rgba(0,0,0,.12)',boxShadow:'0 2px 10px rgba(0,0,0,.18)',
            backdropFilter:'blur(10px)',cursor:'pointer',
            display:'flex',flexDirection:'column',gap:3,alignItems:'center',justifyContent:'center'}}>
          <span style={{display:'block',width:13,height:1.5,background:'#1C1C1A',borderRadius:1}}/>
          <span style={{display:'block',width:13,height:1.5,background:'#1C1C1A',borderRadius:1}}/>
          <span style={{display:'block',width:13,height:1.5,background:'#1C1C1A',borderRadius:1}}/>
        </button>
      </div>

      {/* ── ZU ZWEIT: swipeable MP carousel ── */}
      {isZuzweit&&carouselItems.length>0&&(
        <div className="carousel-wrap"
          onTouchStart={onDragStart} onTouchEnd={onDragEnd}
          onMouseDown={onDragStart} onMouseUp={onDragEnd}>

          {data.hoursAhead>0&&(
            <div className="hours-ahead-banner" style={{margin:'0 12px 8px'}}>
              <span className="hours-ahead-icon">🕐</span>
              <span>{t.hoursAheadBanner(data.hoursAhead)}</span>
            </div>
          )}

          <div className="carousel-track">
            {carouselItems.map((mp,i)=>{
              const offset=i-activeIdx;
              const isActive=offset===0;
              if(Math.abs(offset)>1)return null;
              const spread=mp.spread;
              const spreadClass=spread!=null&&spread<=10?'dot-green':spread!=null&&spread<=25?'dot-amber':'dot-grey';
              const spreadLabel=spread!=null?(spread<=5?'Arrive together':`Within ${spread} min`):'Meeting point';

              return(
                <motion.div key={i} className="carousel-card"
                  style={{position:'absolute',bottom:0,left:0,right:0}}
                  animate={{x:`${offset*92}%`,scale:isActive?1:.92,opacity:isActive?1:.3,zIndex:isActive?10:5}}
                  transition={{type:'spring',stiffness:300,damping:30}}
                  onClick={!isActive?()=>goToCard(i):undefined}>

                  {isActive&&(
                    <div className="carousel-dots">
                      {carouselItems.slice(0,6).map((_,di)=>(
                        <div key={di} className={`carousel-dot ${di===activeIdx?'active':''}`}/>
                      ))}
                    </div>
                  )}

                  {/* Tappable area → detail page */}
                  <div onClick={isActive?()=>setShowDetail(mp):undefined}
                    style={{cursor:isActive?'pointer':'default'}}>
                    <h2 className="rc-title">
                      {mp.station.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').toUpperCase()}.
                    </h2>
                    <div className="rc-meta-row">
                      <span className={`rc-status-dot ${spreadClass}`}/>
                      <span className="rc-meta-text">{spreadLabel}</span>
                      <span className="rc-meta-sep">·</span>
                      <span className="rc-meta-text">{mp.personDetails?.length} people</span>
                    </div>
                  </div>

                  {isActive&&(
                    <>
                      <div className="rc-divider"/>
                      {mp.personDetails?.map((pd,pi)=>{
                        const col=PERSON_COLORS[pi]||PERSON_COLORS[0];
                        const train=pd.trains?.[0];
                        return(
                          <div key={pi} className="rc-person-row" onClick={()=>setShowDetail(mp)} style={{cursor:'pointer'}}>
                            <div className="rc-person-icon" style={{background:col+'22',color:col}}>
                              {String.fromCharCode(65+pi)}
                            </div>
                            <div className="rc-person-body">
                              <div className="rc-action-label">{pd.stationName}</div>
                              {train&&(
                                <div className="rc-action-sub">
                                  <LineBadge line={train.line} category={train.category}/>
                                  <span>dep {train.departure}</span>
                                  {pd.travelMins&&<span>· ~{pd.arrivalTime}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div className="rc-divider"/>
                      <div className="action-tiles">
                        <a className="action-tile" href={taUrl(mp.station,'attractions')} target="_blank" rel="noreferrer">
                          <span className="at-icon">🗺</span><span className="at-lbl">Explore</span>
                        </a>
                        <a className="action-tile" href={taUrl(mp.station,'restaurants')} target="_blank" rel="noreferrer">
                          <span className="at-icon">🍽</span><span className="at-lbl">Eat</span>
                        </a>
                        <button className="action-tile" onClick={()=>setShowDetail(mp)}>
                          <span className="at-icon">🚆</span><span className="at-lbl">Details</span>
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SOWEIT / TAGESAUSFLUG: swipeable train carousel ── */}
      {isSoweit&&data.station&&(
        <>
          {loadingTrains&&(
            <div style={{position:'absolute',bottom:80,left:0,right:0,display:'flex',justifyContent:'center',zIndex:10}}>
              <div style={{background:'rgba(255,255,255,.9)',borderRadius:100,padding:'8px 16px',display:'flex',gap:8,alignItems:'center'}}>
                <div className="dots"><span/><span/><span/></div>
                <span style={{fontSize:12,color:'#555'}}>Loading trains…</span>
              </div>
            </div>
          )}
          {/* Hop selector — always visible above carousel when trains loaded */}
          {!loadingTrains&&trains.length>0&&(
            <div className="hop-selector-bar">
              {[{v:0,l:'Direct'},{v:1,l:'+1 stop'},{v:2,l:'+2 stops'}].map(h=>(
                <button key={h.v} className={`hop-selector-btn${activeHops===h.v?' active':''}`}
                  onPointerDown={e=>{e.stopPropagation();loadTrains(data.station,0,h.v);}}>
                  {h.l}
                </button>
              ))}
            </div>
          )}
          {!loadingTrains&&trains.length>0&&(
            <div className="carousel-wrap"
              onTouchStart={onDragStart} onTouchEnd={onDragEnd}
              onMouseDown={onDragStart} onMouseUp={onDragEnd}>
            <div className="carousel-track">
                {trains.map((train,i)=>{
                  const offset=i-activeIdx;
                  const isActive=offset===0;
                  if(Math.abs(offset)>1)return null;
                  const dest=train.finalDestination.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'');
                  return(
                    <motion.div key={train.id} className="carousel-card"
                      style={{position:'absolute',bottom:0,left:0,right:0}}
                      animate={{x:`${offset*92}%`,scale:isActive?1:.92,opacity:isActive?1:.3,zIndex:isActive?10:5}}
                      transition={{type:'spring',stiffness:300,damping:30}}
                      onClick={!isActive?()=>goToCard(i):undefined}>

                      {isActive&&(
                        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginBottom:8}}>
                          <button onPointerDown={e=>{e.stopPropagation();goToCard(activeIdx-1);}}
                            disabled={activeIdx===0}
                            style={{background:'none',border:'none',cursor:'pointer',color:activeIdx===0?'var(--faint)':'var(--ink)',fontSize:18,padding:'0 2px',lineHeight:1,opacity:activeIdx===0?.3:1}}>‹</button>
                          <div className="carousel-dots" style={{margin:0}}>
                            {trains.slice(0,6).map((_,di)=>(
                              <div key={di} className={`carousel-dot ${di===activeIdx?'active':''}`}/>
                            ))}
                          </div>
                          <button onPointerDown={e=>{e.stopPropagation();goToCard(activeIdx+1);}}
                            disabled={activeIdx>=trains.length-1}
                            style={{background:'none',border:'none',cursor:'pointer',color:activeIdx>=trains.length-1?'var(--faint)':'var(--ink)',fontSize:18,padding:'0 2px',lineHeight:1,opacity:activeIdx>=trains.length-1?.3:1}}>›</button>
                        </div>
                      )}

                      <div onClick={isActive?()=>setDetailTrain(train):undefined} style={{cursor:isActive?'pointer':'default'}}>
                        <h2 className="rc-title">{dest.toUpperCase()}.</h2>
                        <div className="rc-meta-row">
                          <span className="rc-status-dot dot-green"/>
                          <LineBadge line={train.line} category={train.category}/>
                          <span className="rc-meta-sep">·</span>
                          <span className="rc-meta-text">dep {train.departure}{train.laterCount>0&&<span style={{color:'var(--faint)'}}> · +{train.laterCount} later</span>}</span>
                          <span className="rc-meta-sep">·</span>
                          <span className="rc-meta-text">{t.platform} {train.platform}</span>
                        </div>
                      </div>

                      {isActive&&(
                        <>
                          <div className="rc-divider"/>
                          <div className="rc-stops-label" style={{marginTop:4}}>{t.stops}</div>
                          <div style={{fontSize:11,color:'#888',lineHeight:1.6,marginBottom:12}}>
                            {train.allStops.slice(0,4).join(' · ')}{train.allStops.length>4&&` +${train.allStops.length-4} more`}
                          </div>
                          <div className="action-tiles">
                            <a className="action-tile" href={taUrl(train.finalDestination,'attractions')} target="_blank" rel="noreferrer">
                              <span className="at-icon">🗺</span><span className="at-lbl">Explore</span>
                            </a>
                            <a className="action-tile" href={taUrl(train.finalDestination,'restaurants')} target="_blank" rel="noreferrer">
                              <span className="at-icon">🍽</span><span className="at-lbl">Eat</span>
                            </a>
                            <button className="action-tile" onClick={()=>setDetailTrain(train)}>
                              <span className="at-icon">🚆</span><span className="at-lbl">Details</span>
                            </button>
                          </div>
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
          {!loadingTrains&&trains.length===0&&(
            <div className="inset-card" style={{padding:'20px 18px'}}>
              <p style={{fontSize:13,color:'#888',textAlign:'center'}}>{t.noTrains}</p>
            </div>
          )}
        </>
      )}

      {/* ── OTHER MODES: inset card with reachability ── */}
      {!isZuzweit&&!isSoweit&&data.station&&(
        <motion.div className="inset-card" style={{maxHeight:'62vh',display:'flex',flexDirection:'column'}}
          initial={{y:60,opacity:0}} animate={{y:0,opacity:1}}
          transition={{type:'spring',stiffness:260,damping:30}}>
          <div className="sheet-handle-row"><div className="sheet-handle"/></div>
          <div className="rc-scroll">
            <ReachablePanel evaNo={data.station.eva} stationName={data.station.name} t={t}/>
          </div>
        </motion.div>
      )}
      {/* ── MP DETAIL OVERLAY — slides up over existing map, no remount ── */}
      <AnimatePresence>
        {showDetail&&(
          <>
            <motion.div
              style={{position:'absolute',inset:0,background:'rgba(0,0,0,.15)',zIndex:90,pointerEvents:'none'}}
              initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              transition={{duration:.2}}/>
            <motion.div
              style={{position:'absolute',bottom:14,left:12,right:12,zIndex:100,background:'var(--card)',borderRadius:20}}
              initial={{y:'100%',opacity:0}}
              animate={{y:0,opacity:1}}
              exit={{y:'100%',opacity:0}}
              transition={{type:'spring',stiffness:340,damping:36}}>
              <div className="sheet-handle-row"><div className="sheet-handle"/></div>
              <div className="rc-scroll" style={{maxHeight:'60vh'}}>
                <h2 className="rc-title">
                  {showDetail.station.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').toUpperCase()}.
                </h2>
                <div className="rc-meta-row">
                  <span className={`rc-status-dot ${showDetail.spread!=null&&showDetail.spread<=10?'dot-green':showDetail.spread!=null&&showDetail.spread<=25?'dot-amber':'dot-grey'}`}/>
                  <span className="rc-meta-text">
                    {showDetail.spread!=null?(showDetail.spread<=5?'Arrive together':`Within ${showDetail.spread} min`):'Meeting point'}
                  </span>
                  <span className="rc-meta-sep">·</span>
                  <span className="rc-meta-text">{showDetail.personDetails?.length} people</span>
                </div>
                <div className="rc-divider"/>
                {showDetail.personDetails?.map((pd,pi)=>{
                  const col=PERSON_COLORS[pi]||PERSON_COLORS[0];
                  const train=pd.trains?.[0];
                  const shortName=showDetail.station.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').trim();
                  return(
                    <div key={pi} className="journey-card" style={{borderColor:col+'33'}}>
                      <div className="journey-card-header">
                        <div className="rc-person-icon" style={{background:col+'22',color:col}}>
                          {String.fromCharCode(65+pi)}
                        </div>
                        <div style={{flex:1}}>
                          <div className="rc-action-label">{pd.stationName}</div>
                          <div style={{fontSize:10,color:'#999'}}>Departing {train?.departure||'—'}</div>
                        </div>
                        {pd.arrivalTime&&(
                          <div style={{textAlign:'right'}}>
                            <div style={{fontFamily:"'Fraunces',serif",fontSize:15,fontWeight:700,color:'var(--ink)'}}>~{pd.arrivalTime}</div>
                            <div style={{fontSize:10,color:'#999'}}>arrives</div>
                          </div>
                        )}
                      </div>
                      {train&&(
                        <div className="journey-train-row">
                          <LineBadge line={train.line} category={train.category}/>
                          <span style={{fontSize:11,color:'var(--muted)',flex:1,marginLeft:6}}>
                            {pd.stationName.replace(/ (Hbf|Hauptbahnhof).*/,'')} → {shortName}
                          </span>
                          <span style={{fontFamily:"'Fraunces',serif",fontSize:12,fontWeight:600,color:col}}>{train.departure}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="rc-divider"/>
                <div className="action-tiles">
                  <a className="action-tile" href={taUrl(showDetail.station,'attractions')} target="_blank" rel="noreferrer">
                    <span className="at-icon">🗺</span><span className="at-lbl">Explore</span>
                  </a>
                  <a className="action-tile" href={taUrl(showDetail.station,'restaurants')} target="_blank" rel="noreferrer">
                    <span className="at-icon">🍽</span><span className="at-lbl">Eat</span>
                  </a>
                </div>
              </div>
              <div className="inset-card-footer">
                <button className="footer-btn footer-btn-accent" style={{width:'100%'}}
                  onClick={()=>onSaveToggle({id:showDetail.station,station:showDetail.station,line:'',category:''},null)}>
                  ♡ Save trip
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Modals — rendered inside results so map stays mounted */}
      {resMenuOpen&&<HamburgerMenu open={resMenuOpen} onClose={()=>setResMenuOpen(false)}
        lang={'en'} setLang={()=>{}} dark={dark} setDark={()=>{}}
        onMode={()=>{onBack();}} t={t}
        savedTrips={savedTrips} onSavedOpen={()=>{setResMenuOpen(false);setResShowSaved(true);}}/>}
      {resShowSaved&&<SavedTripsSheet trips={savedTrips} onRemove={tr=>onSaveToggle(tr,tr.fromStation)} onClose={()=>setResShowSaved(false)} t={t}/>}
    </div>
  );
}



// ── Coming Soon modal ────────────────────────────────────────────────────────
function ComingSoonModal({mode,onClose,t}){
  const [sent,setSent]=useState(false);
  const modeLabel=t.modes.find(m=>m.id===mode)?.label||mode;
  const handleInterest=()=>{
    // Track interest — fires analytics event if Plausible is connected
    trackEvent('feature_interest',{mode});
    setSent(true);
  };
  return(
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        style={{position:'fixed',inset:0,zIndex:500,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'flex-end',justifyContent:'center',padding:'0 12px 24px'}}
        onClick={onClose}>
        <motion.div initial={{y:60,opacity:0}} animate={{y:0,opacity:1}} exit={{y:60,opacity:0}}
          transition={{type:'spring',stiffness:300,damping:30}}
          onClick={e=>e.stopPropagation()}
          style={{background:'var(--surface)',borderRadius:24,padding:'24px 20px',width:'100%',maxWidth:420,boxShadow:'0 8px 40px rgba(0,0,0,.18)'}}>
          <div style={{fontSize:32,marginBottom:12,textAlign:'center'}}>🚧</div>
          <div style={{fontFamily:"'Fraunces',serif",fontSize:22,fontWeight:700,color:'var(--ink)',marginBottom:6,textAlign:'center'}}>
            {modeLabel} — coming soon
          </div>
          <div style={{fontSize:13,color:'var(--muted)',lineHeight:1.6,marginBottom:20,textAlign:'center'}}>
            {mode==='tagesausflug'
              ? "Day trip suggestions with curated destinations — castles, lakes, hiking — ranked by travel time."
              : "Discover what's worth stopping at along your route. Get off, explore, continue."}
          </div>
          {!sent?(
            <button onClick={handleInterest} style={{width:'100%',padding:'13px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:14,fontFamily:"'Fraunces',serif",fontSize:15,fontWeight:700,cursor:'pointer',marginBottom:10}}>
              I want this feature →
            </button>
          ):(
            <div style={{textAlign:'center',padding:'12px',background:'var(--green-lt)',borderRadius:12,marginBottom:10}}>
              <div style={{fontSize:18,marginBottom:4}}>✓</div>
              <div style={{fontSize:13,color:'var(--green-dk)',fontWeight:500}}>Thanks! We'll build it soon.</div>
            </div>
          )}
          <button onClick={onClose} style={{width:'100%',padding:'11px',background:'var(--surf2)',color:'var(--muted)',border:'none',borderRadius:14,fontSize:13,cursor:'pointer'}}>
            Back to Zu zweit & So weit
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}


// ── Saved Trips Sheet ────────────────────────────────────────────────────────
function SavedTripsSheet({trips,onRemove,onClose,t}){
  return(
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        style={{position:'fixed',inset:0,zIndex:400,background:'rgba(0,0,0,.45)'}}
        onClick={onClose}/>
      <motion.div initial={{y:'100%'}} animate={{y:0}} exit={{y:'100%'}}
        transition={{type:'spring',stiffness:300,damping:32}}
        style={{position:'fixed',bottom:0,left:0,right:0,zIndex:401,
          background:'var(--surface)',borderRadius:'20px 20px 0 0',
          maxHeight:'75vh',display:'flex',flexDirection:'column',
          boxShadow:'0 -4px 32px rgba(0,0,0,.18)'}}>
        {/* Handle */}
        <div style={{padding:'10px 0 4px',display:'flex',justifyContent:'center'}}>
          <div style={{width:36,height:4,borderRadius:2,background:'var(--bdr2)'}}/>
        </div>
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'8px 18px 12px',borderBottom:'1px solid var(--bdr)'}}>
          <span style={{fontFamily:"'Fraunces',serif",fontSize:18,fontWeight:600,color:'var(--ink)'}}>
            ♥ Saved trips
          </span>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',
            fontSize:18,color:'var(--muted)',padding:'4px 8px'}}>✕</button>
        </div>
        {/* List */}
        <div style={{flex:1,overflowY:'auto',padding:'12px 16px 24px'}}>
          {trips.length===0?(
            <div style={{textAlign:'center',padding:'32px 0',color:'var(--muted)',fontSize:13}}>
              No saved trips yet.<br/>
              <span style={{fontSize:12,color:'var(--faint)'}}>Tap ♡ on any destination to save it.</span>
            </div>
          ):trips.map((trip,i)=>(
            <div key={trip.id||i} style={{display:'flex',alignItems:'center',gap:12,
              padding:'12px 14px',background:'var(--surf2)',borderRadius:14,marginBottom:8}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:"'Fraunces',serif",fontSize:15,fontWeight:600,
                  color:'var(--ink)',marginBottom:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {(trip.station||trip.finalDestination||trip.id||'').replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').toUpperCase()}.
                </div>
                <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  {trip.line&&<span style={{fontSize:10,fontWeight:600,padding:'2px 6px',
                    borderRadius:4,background:'var(--re-bg)',color:'var(--re-fg)'}}>{trip.line}</span>}
                  {trip.fromStation&&<span style={{fontSize:11,color:'var(--muted)'}}>from {trip.fromStation}</span>}
                  {trip.departure&&<span style={{fontSize:11,color:'var(--muted)'}}>dep {trip.departure}</span>}
                </div>
              </div>
              <div style={{display:'flex',gap:8,flexShrink:0}}>
                <a href={`https://www.tripadvisor.com/Attractions-${(trip.station||trip.finalDestination||'').replace(/\s+/g,'+')}.html`}
                  target="_blank" rel="noreferrer"
                  style={{fontSize:18,textDecoration:'none'}} title="Explore">🗺</a>
                <button onClick={()=>onRemove(trip)}
                  style={{background:'none',border:'none',cursor:'pointer',
                    fontSize:18,color:'var(--accent)',padding:0}} title="Remove">♥</button>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── STEP 1: HERO ─────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function StepHero({mode,setMode,onContinue,savedTrips,t,menuOpen,setMenuOpen}){
  const [comingSoon,setComingSoon]=useState(null);

  return(<>
    <motion.div className="step-hero" key="hero"
      initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
      exit={{opacity:0,transition:{duration:.15}}}
      transition={{type:'spring',stiffness:320,damping:32}}>

      {/* Static hero backdrop — the old Railway-hosted mp4 is gone (dead link
          left a grey box + eternal spinner). A layered gradient keeps the mood
          with zero network dependency; swap in a self-hosted poster/video later. */}
      <div className="hero-video" aria-hidden="true">
        <div style={{
          width:'100%',height:'100%',
          background:'radial-gradient(120% 90% at 75% 10%, #3d2f28 0%, #241d19 45%, #14100e 100%)',
        }}/>
      </div>
      <div className="hero-overlay"/>

      <div className="hero-nav">
        <span className="hero-logo">zwischen</span>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <AnimatePresence>
            {savedTrips.length>0&&(
              <motion.div className="saved-nav"
                initial={{scale:0,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0,opacity:0}}
                transition={{type:"spring",stiffness:400,damping:20}}>
                ♥ {savedTrips.length}
              </motion.div>
            )}
          </AnimatePresence>
          <button className="hero-hamburger" onClick={()=>setMenuOpen(true)}>
            <span/><span/><span/>
          </button>
        </div>
      </div>

      <motion.div className="hero-text" variants={stagger} initial="hidden" animate="show">
        <motion.div variants={fadeUp} className="hero-eyebrow">{t.eyebrow}</motion.div>
        <motion.h1 variants={fadeUp} className="hero-title">
          {t.title[0]}<br/><em>{t.title[1]}</em><br/>{t.title[2]}
        </motion.h1>
        <motion.p variants={fadeUp} className="hero-sub">{t.sub}</motion.p>
      </motion.div>

      <motion.div className="hero-card"
        initial={{y:60,opacity:0}} animate={{y:0,opacity:1}}
        transition={{delay:.2,type:"spring",stiffness:260,damping:28}}>
        <div className="hero-card-body">
          <p className="select-mode-label">{t.selectMode}</p>
          <motion.div className="mode-grid" variants={stagger} initial="hidden" animate="show">
            {t.modes.map(m=>(
              <motion.button key={m.id} variants={slideUp}
                className={`mode-card ${mode===m.id?"active":""} ${m.soon?"mode-card-soon":""}`}
                onClick={()=>m.soon?setComingSoon(m.id):setMode(m.id)} whileTap={{scale:.96}}>
                <span className="mode-icon">{m.icon}</span>
                <div style={{position:'relative',display:'inline-block'}}>
                  <span className="mode-label">{m.label}</span>
                  {m.soon&&<span className="soon-badge">Soon</span>}
                </div>
                <span className="mode-desc">{m.desc}</span>
                {mode===m.id&&(
                  <motion.div className="mode-active-ring" layoutId="mode-ring"
                    transition={{type:"spring",stiffness:400,damping:30}}/>
                )}
              </motion.button>
            ))}
          </motion.div>
          <motion.button className="cta-btn" onClick={onContinue} whileTap={{scale:.97}}
            initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}
            transition={{delay:.4,type:"spring",stiffness:300,damping:26}}>
            {t.continue} →
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
    {comingSoon&&<ComingSoonModal mode={comingSoon} onClose={()=>setComingSoon(null)} t={t}/>}
  </>);
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── STEP 2: MAP + INPUTS ─────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function StepMap({mode,setMode,dark,t,onResults,onBack,initialStation}){
  const onModeChange=id=>{setMode(id);trackEvent('mode_select',{mode:id});};
  // Single-origin state
  const [queryA,setQueryA]=useState('');
  const [stationA,setStationA]=useState(null);
  const [stationAGeo,setStationAGeo]=useState(null);
  const [hops,setHops]=useState(0);

  // Pre-fill station from landing page context (via initialStation prop)
  useEffect(()=>{
    if(!initialStation)return;
    // Set text input immediately
    setQueryA(initialStation);
    // Fetch full station object so Explore button works
    apiFetch(`${API_BASE}/stations?q=${encodeURIComponent(initialStation)}`)
      .then(r=>r.json())
      .then(d=>{const s=d.stations&&d.stations[0];if(s){setStationA(s);setQueryA(s.name);}})
      .catch(()=>{});
  },[initialStation]);
  const [hourOffset,setHourOffset]=useState(0);

  // Zu zweit state
  const [persons,setPersons]=useState([
    {id:0,query:'',station:null,geo:null},
    {id:1,query:'',station:null,geo:null},
  ]);
  const [mpLoading,setMpLoading]=useState(false);
  const [mpError,setMpError]=useState('');

  // Map state
  const [mapFly,setMapFly]=useState(null);
  const [mapFit,setMapFit]=useState(null);
  const [mapLines,setMapLines]=useState([]);
  const [originGeos,setOriginGeos]=useState({});

  // Bottom sheet drag
  const [sheetOpen,setSheetOpen]=useState(true);
  const [sheetComingSoon,setSheetComingSoon]=useState(null);
  const sheetY=useMotionValue(0);

  const isZuzweit=mode==='zuzweit';
  const ready=isZuzweit
    ? persons.filter(p=>p.station).length>=2
    : !!stationA;

  const selectPersonStation=async(idx,s)=>{
    setPersons(prev=>prev.map((p,i)=>i===idx?{...p,query:s.name,station:s}:p));
    const geo=await geocodeOne(s.name);
    if(geo){
      setPersons(prev=>prev.map((p,i)=>i===idx?{...p,geo}:p));
      setMapFly({c:geo,z:9});
      setOriginGeos(prev=>({...prev,[s.name]:geo}));
    }
  };

  const selectSingleStation=async(s)=>{
    setStationA(s);setQueryA(s.name);
    const geo=await geocodeOne(s.name);
    if(geo){setStationAGeo(geo);setMapFly({c:geo,z:11});}
  };

  const handleCTA=async()=>{
    if(isZuzweit){
      // Find meeting points
      const valid=persons.filter(p=>p.station);
      setMpLoading(true);setMpError('');setMapLines([]);
      try{
        const r=await apiFetch(`${API_BASE}/midpoint?evas=${valid.map(p=>p.station.eva).join(',')}&hops=${hops}`);
        if(!r.ok)throw new Error(`HTTP ${r.status}`);
        const d=await r.json();
        const mps=d.meetingPoints||[];
        if(!mps.length){setMpError(t.noResults);setMpLoading(false);return;}

        // Geocode for map lines
        const mpNames=mps.map(m=>m.station).slice(0,10);
        const originNames=valid.map(p=>p.station.name);
        const allG=await geocodeBatch([...originNames,...mpNames]);
        const mpGeos={};
        mpNames.forEach(n=>{if(allG[n])mpGeos[n]=allG[n];});
        const oGeos={};
        originNames.forEach(n=>{if(allG[n])oGeos[n]=allG[n];});

        // Draw lines
        const lines=[];
        originNames.forEach((on,oi)=>{
          const og=allG[on];if(!og)return;
          mpNames.slice(0,6).forEach(mn=>{const mg=allG[mn];if(!mg)return;
            lines.push({from:og,to:mg,color:PERSON_COLORS[oi%PERSON_COLORS.length],op:.55,weight:2.5});
          });
        });
        setMapLines(lines);

        // Fit map
        const all=Object.values(allG);
        if(all.length>=2){
          const lts=all.map(c=>c[0]),lns=all.map(c=>c[1]);
          setMapFit([[Math.min(...lts)-.1,Math.min(...lns)-.15],[Math.max(...lts)+.1,Math.max(...lns)+.15]]);
        }

        // Merge geocoded coords back into persons array
        const personsWithGeo = persons.map(p=>({
          ...p,
          geo: p.geo || oGeos[p.station?.name] || null
        }));

        // Pass to results now that geocoding is done
        onResults({mode,persons:personsWithGeo,mps,mpGeos,oGeos,mapLines:lines,hoursAhead:d.hoursAhead||0,mapFit:
          all.length>=2?[[Math.min(...all.map(c=>c[0]))-.1,Math.min(...all.map(c=>c[1]))-.15],[Math.max(...all.map(c=>c[0]))+.1,Math.max(...all.map(c=>c[1]))+.15]]:null
        });
      }catch(e){setMpError('Could not find meeting points. Check your connection and try again.');trackError('midpoint',e);}
      setMpLoading(false);
    } else {
      // Single origin → departures
      onResults({mode,station:stationA,hops,hourOffset,stationGeo:stationAGeo});
    }
  };

  const mapCenter=[51.2,10.5];

  return(<>
    <motion.div className="step-map" key="map"
      initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-40}}
      transition={{type:'spring',stiffness:300,damping:30}}>

      {/* Full-screen map */}
      <div className="map-full">
        <MapContainer center={mapCenter} zoom={6} style={{width:'100%',height:'100%'}} zoomControl={false} attributionControl={false}>
          <TileLayer
            url={dark?`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            attribution="© OpenStreetMap © CARTO" subdomains="abcd" maxZoom={20}/>
          <TileLayer
            url={dark?`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            subdomains="abcd" maxZoom={20} pane="shadowPane"/>
          <MapFly to={mapFly}/>
          <MapFit bounds={mapFit}/>
          {/* Single station */}
          {stationAGeo&&<Marker position={stationAGeo} icon={mkDot(PERSON_COLORS[0],16)}><Popup>{stationA?.name}</Popup></Marker>}
          {/* Person markers */}
          {persons.map((p,i)=>p.geo&&(
            <Marker key={i} position={p.geo} icon={mkDot(PERSON_COLORS[i%PERSON_COLORS.length],14)}>
              <Popup>{p.station?.name}</Popup>
            </Marker>
          ))}
          {/* Lines */}
          {mapLines.map((l,i)=>(
            <Polyline key={i} positions={[l.from,l.to]}
              pathOptions={{color:l.color,weight:l.weight||2.5,opacity:l.op||.55,dashArray:'6 5'}}/>
          ))}
        </MapContainer>

        <button className="map-back-btn" onClick={onBack}>← {t.back}</button>

        {/* Step dots */}
        <div className="map-step-dots"><StepDots step={1}/></div>
      </div>

      {/* ── Bottom sheet ── */}
      <motion.div className="bottom-sheet"
        initial={{y:80,opacity:0}} animate={{y:0,opacity:1}}
        transition={{delay:.15,type:'spring',stiffness:240,damping:26}}>

        {/* Drag handle */}
        <div className="sheet-handle-row">
          <div className="sheet-handle"/>
        </div>

        {/* Mode tab bar — like the Trips/Search tabs in the reference */}
        <div className="sheet-tabs">
          {t.modes.map(m2=>(
            <button key={m2.id}
              className={`sheet-tab ${mode===m2.id?'active':''}`}
              onClick={()=>{ if(m2.soon){setSheetComingSoon(m2.id);} else onModeChange(m2.id); }}>
              {m2.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={mode} className="sheet-body"
            initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0}}
            transition={{duration:.18}}>

            {/* Title row */}
            <div className="sheet-title">
              {isZuzweit ? t.findMidpoint : t.modes.find(m2=>m2.id===mode)?.desc}
            </div>

            {/* Station input cards — styled like the LAX/LHR info cards */}
            {!isZuzweit ? (
              <div className="sheet-cards">
                <div className={`station-card ${stationA?'filled':''}`}>
                  <span className="station-card-lbl">
                    <span className="station-card-icon">⊙</span>
                    {mode==='entlang'?t.from:t.startingFrom}
                  </span>
                  <StationInput value={queryA} onChange={setQueryA} onSelect={selectSingleStation}
                    placeholder={t.placeholder} autoFocus/>
                </div>
                {mode==='entlang'&&(
                  <div className="station-card">
                    <span className="station-card-lbl">
                      <span className="station-card-icon">⊙</span>
                      {t.to}
                    </span>
                    <StationInput value="" onChange={()=>{}} onSelect={()=>{}}
                      placeholder={t.placeholderDest} color="#1D4ED8"/>
                  </div>
                )}
              </div>
            ) : (
              <div className="sheet-cards">
                {/* First two persons side by side */}
                <div style={{display:'flex',gap:8}}>
                  {persons.slice(0,2).map((p,i)=>(
                    <div key={p.id} className={`station-card ${p.station?'filled':''}`} style={{flex:1,minWidth:0}}>
                      <span className="station-card-lbl">
                        <span className="station-card-dot" style={{background:PERSON_COLORS[i%PERSON_COLORS.length]}}/>
                        {i===0?'Person A':'Person B'}
                      </span>
                      <StationInput
                        value={p.query}
                        onChange={v=>setPersons(prev=>prev.map((x,xi)=>xi===i?{...x,query:v}:x))}
                        onSelect={s=>selectPersonStation(i,s)}
                        placeholder={i===0?'e.g. München':'e.g. Berlin'}
                        color={PERSON_COLORS[i%PERSON_COLORS.length]}/>
                    </div>
                  ))}
                </div>
                {/* Extra persons stacked below */}
                {persons.slice(2).map((p,ii)=>{const i=ii+2;return(
                  <div key={p.id} className={`station-card ${p.station?'filled':''}`}>
                    <span className="station-card-lbl">
                      <span className="station-card-dot" style={{background:PERSON_COLORS[i%PERSON_COLORS.length]}}/>
                      {`Person ${String.fromCharCode(65+i)}`}
                    </span>
                    <StationInput
                      value={p.query}
                      onChange={v=>setPersons(prev=>prev.map((x,xi)=>xi===i?{...x,query:v}:x))}
                      onSelect={s=>selectPersonStation(i,s)}
                      placeholder={t.placeholderB}
                      color={PERSON_COLORS[i%PERSON_COLORS.length]}/>
                  </div>
                );})}
                {/* Add person — full width tertiary button */}
                {persons.length<4&&(
                  <button className="add-person-btn-wide"
                    onClick={()=>setPersons(prev=>[...prev,{id:prev.length,query:'',station:null,geo:null}])}>
                    + Add another person
                  </button>
                )}
              </div>
            )}

            {/* Hop pills */}
            {!isZuzweit&&(
              <div className="sheet-hops">
                {[0,1,2].map(h=>(
                  <motion.button key={h} className={`hop-chip ${hops===h?'active':''}`}
                    onClick={()=>setHops(h)} whileTap={{scale:.93}}>
                    {h===0?t.direct:h===1?t.hop1:t.hop2}
                  </motion.button>
                ))}
              </div>
            )}

            {mpError&&<div className="error-msg">{mpError}</div>}

            {/* Why-is-this-disabled hint: user typed something but hasn't
                selected a suggestion yet */}
            {!ready&&!isZuzweit&&queryA.trim().length>=2&&(
              <div className="error-msg" style={{color:'var(--faint)'}}>{t.selectHint}</div>
            )}

            {/* CTA */}
            <motion.button className="sheet-cta"
              disabled={!ready||mpLoading}
              onClick={handleCTA}
              whileTap={{scale:.97}}
              animate={{
                background: ready?'var(--accent)':'var(--surf3)',
                color: ready?'#fff':'var(--faint)',
              }}
              transition={{duration:.2}}>
              {mpLoading
                ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                    <span className="dots" style={{display:'inline-flex',gap:4}}><span/><span/><span/></span>
                    Finding…
                  </span>
                : <>{isZuzweit?t.findMidpoint:t.explore} →</>
              }
            </motion.button>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </motion.div>
    {sheetComingSoon&&<ComingSoonModal mode={sheetComingSoon} onClose={()=>setSheetComingSoon(null)} t={t}/>}
  </>);
}




// ══════════════════════════════════════════════════════════════════════════════
// ─── DESKTOP LAYOUT (≥768px) ──────────────────────────────────────────────────
// Variant C: full-bleed map + floating 343px left panel
// Mobile (≤767px) is completely untouched — this only renders above 768px
// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// ─── DESKTOP LAYOUT (≥768px) ──────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
function DesktopLayout({initialStation,mode,setMode,step,setStep,resultsData,setResultsData,
  savedTrips,onSaveToggle,dark,t,lang,setLang,menuOpen,setMenuOpen,aboutOpen,setAboutOpen}){
  // Local origin geo — filled async after results arrive
  const [originGeo,setOriginGeo]=useState(null);
  const [dtComingSoon,setDtComingSoon]=useState(null);
  const [showSaved,setShowSaved]=useState(false);
  // visualViewport-keyboard: shift bottom sheet up when keyboard opens
  useEffect(()=>{
    const vv = window.visualViewport;
    if(!vv) return;
    const onResize = () => {
      const kbHeight = window.innerHeight - vv.height - vv.offsetTop;
      document.documentElement.style.setProperty('--keyboard-height', Math.max(0, kbHeight) + 'px');
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, []);

    // back-to-landing: if user navigates back to /, reload to show landing page
  useEffect(()=>{
    // Push a state so we can detect back navigation
    window.history.replaceState({zwischen: true}, '', window.location.href);
    const onPop = (e) => {
      // User pressed back - force full page reload to show landing page
      window.location.replace('/');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const [expandedIdx,setExpandedIdx]=useState(0);
  const [mapFly,setMapFly]=useState(null);
  const [mapFit,setMapFit]=useState(resultsData?.mapFit||null);
  const [trains,setTrains]=useState([]);
  const [loadingTrains,setLoadingTrains]=useState(false);

  const isZuzweit=mode==='zuzweit';
  const isResults=step===2&&resultsData;
  const [dtDestGeos,setDtDestGeos]=useState({});

  const [dtTreeData,setDtTreeData]=useState(null);
  const [dtExpandedClusters,setDtExpandedClusters]=useState({});
  const [dtHopGeos,setDtHopGeos]=useState({});

  useEffect(()=>{
    if(!isZuzweit&&trains.length>0){
      const names=[...new Set(trains.filter(t=>t.category==='RE').map(t=>t.finalDestination))].slice(0,8);
      geocodeBatch(names).then(g=>setDtDestGeos(g));
    }
  },[trains,isZuzweit]);

  useEffect(()=>{
    if(!isZuzweit&&isResults&&resultsData?.station&&(resultsData.hops||0)>0){
      apiFetch(`${API_BASE}/reachable?eva=${resultsData.station.eva}&hops=${resultsData.hops}`)
        .then(r=>r.json()).then(d=>setDtTreeData(d)).catch(()=>{});
    }
  },[isResults,resultsData]);

  useEffect(()=>{
    if(!dtTreeData)return;
    const hop1=(dtTreeData.byHop[1]?.trains||[]);
    const hop2=(dtTreeData.byHop[2]?.trains||[]);
    const vias=[...new Set([
      ...hop1.map(t=>t.via),
      ...hop2.map(t=>t.via),
      ...hop2.filter(t=>t.via1).map(t=>t.via1),
    ])].slice(0,12);
    const reDests=[...new Set([
      ...hop1.filter(t=>t.category==='RE').map(t=>t.finalDestination),
      ...hop2.filter(t=>t.category==='RE').map(t=>t.finalDestination),
    ])].slice(0,14);
    geocodeBatch([...vias,...reDests]).then(g=>setDtHopGeos(prev=>({...prev,...g})));
  },[dtTreeData]);

  const dtExpandCluster=(viaName,trains)=>{
    setDtExpandedClusters(prev=>({...prev,[viaName]:!prev[viaName]}));
    if(!dtExpandedClusters[viaName]){
      const names=[...new Set(trains.map(t=>t.finalDestination))].slice(0,8);
      geocodeBatch(names).then(g=>setDtHopGeos(prev=>({...prev,...g})));
    }
  };
  const mps=resultsData?.mps||[];
  const carouselItems=isZuzweit?mps:trains;

  const loadTrains=useCallback(async(station,offset=0)=>{
    setLoadingTrains(true);setTrains([]);
    try{
      const r=await apiFetch(`${API_BASE}/timetable?eva=${station.eva}&offset=${offset}`);
      const d=await r.json();
      const stops=d?.timetable?.s||[];
      setTrains(dedupeByDestination(stops.filter(s=>['RE','RB','S','ARV'].includes(s?.tl?.c)&&s.dp)
        .map(s=>({id:s.id,line:s.dp?.l||s.dp?.fb||`${s.tl.c} ${s.tl.n}`,category:s.tl.c,
          departure:fmtTime(s.dp.pt),platform:s.dp.pp,
          allStops:s.dp.ppth?s.dp.ppth.split('|'):[],
          finalDestination:s.dp.ppth?s.dp.ppth.split('|').pop():'Unknown'}))
        .sort((a,b)=>{const cd=(CAT_PRI[a.category]??9)-(CAT_PRI[b.category]??9);return cd!==0?cd:a.departure.localeCompare(b.departure);})));
    }catch{}
    setLoadingTrains(false);
  },[]);

  useEffect(()=>{
    if(isResults&&!isZuzweit&&resultsData.station) loadTrains(resultsData.station);
    if(isResults&&resultsData.mapFit) setMapFit(resultsData.mapFit);
    setExpandedIdx(0);
    // Geocode origin if missing
    if(isResults&&!isZuzweit&&!resultsData.stationGeo&&resultsData.station){
      geocodeOne(resultsData.station.name).then(g=>{ if(g) setOriginGeo(g); });
    } else if(isResults&&resultsData.stationGeo){
      setOriginGeo(resultsData.stationGeo);
    }
  },[resultsData]);

  useEffect(()=>{
    if(isResults&&isZuzweit&&mps.length>0){
      const geo=resultsData.mpGeos?.[mps[0].station];
      if(geo)setMapFly({c:geo,z:9});
    }
  },[resultsData,isResults,isZuzweit]);

  useEffect(()=>{
    if(isResults&&!isZuzweit&&(originGeo||resultsData?.stationGeo)){
      const origin=originGeo||resultsData.stationGeo;
      const all=[origin,...Object.values(dtDestGeos),...Object.values(dtHopGeos)].filter(Boolean);
      if(all.length<2)return;
      const lats=all.map(g=>g[0]),lons=all.map(g=>g[1]);
      const pad=(resultsData.hops||0)>0?.4:.12;
      setMapFit([[Math.min(...lats)-pad,Math.min(...lons)-pad],[Math.max(...lats)+pad,Math.max(...lons)+pad]]);
    }
  },[dtDestGeos,dtHopGeos,originGeo]);

  const onCardExpand=(idx)=>{
    setExpandedIdx(idx);
    if(isZuzweit&&mps[idx]){
      const geo=resultsData?.mpGeos?.[mps[idx].station];
      if(geo)setMapFly({c:geo,z:12});
    }
  };

  const mapCenter=(isResults&&(originGeo||resultsData?.stationGeo))||[51.2,10.5];
  const mapZoom=isResults?(isZuzweit?6:9):6;
  const activeMp=isZuzweit?mps[expandedIdx]:null;
  const activeMpGeo=activeMp?resultsData?.mpGeos?.[activeMp.station]:null;

  // Tagesausflug results → GTFSExplorer (full-screen, self-contained nav/modes/search)
  if(isResults&&resultsData.mode==='tagesausflug'&&resultsData.station&&resultsData.stationGeo){
    return(
      <GTFSExplorer
        originStation={{ name: resultsData.station.name, eva: resultsData.station.eva, lat: resultsData.stationGeo[0], lng: resultsData.stationGeo[1] }}
        apiFetch={apiFetch}
        API_BASE={API_BASE}
        lang={lang} setLang={setLang}
        onModeChange={(m)=>{ setMode(m); setStep(0); setResultsData(null); }}/>
    );
  }

  return(
    <div className="dt-root">
      <HamburgerMenu open={menuOpen} onClose={()=>setMenuOpen(false)}
        lang={lang} setLang={setLang} dark={dark} setDark={()=>{}}
        onMode={id=>{if(id==='about'){setAboutOpen(true);}else if(t.modes.find(m=>m.id===id)?.soon){setDtComingSoon(id);}else{setMode(id);setStep(0);setResultsData(null);}}} t={t}
        savedTrips={savedTrips} onSavedOpen={()=>setShowSaved(true)}/>
      <AboutModal open={aboutOpen} onClose={()=>setAboutOpen(false)} t={t}/>
      {dtComingSoon&&<ComingSoonModal mode={dtComingSoon} onClose={()=>setDtComingSoon(null)} t={t}/>}
      {showSaved&&<SavedTripsSheet trips={savedTrips} onRemove={t=>onSaveToggle(t,t.fromStation)} onClose={()=>setShowSaved(false)} t={t}/>}

      {/* ── Full-bleed map ── */}
      <div className="dt-map">
        <MapContainer center={mapCenter} zoom={mapZoom}
          style={{width:'100%',height:'100%'}} zoomControl={false} attributionControl={false}>
          <TileLayer
            url={dark?`https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            attribution="© OpenStreetMap © CARTO" subdomains="abcd" maxZoom={20}/>
          <TileLayer
            url={dark?`https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`:`https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            subdomains="abcd" maxZoom={20} pane="shadowPane"/>
          <MapFly to={mapFly}/>
          <MapFit bounds={mapFit}/>
          {isResults&&isZuzweit&&(
            <>
              {resultsData.persons?.map((p,i)=>{
                const pGeo=p.geo||resultsData.oGeos?.[p.station?.name];
                if(!pGeo)return null;
                return(<Marker key={`dto${i}`} position={pGeo} icon={mkDot(PERSON_COLORS[i],14)}><Popup>{p.station?.name}</Popup></Marker>);
              })}
              {activeMpGeo&&resultsData.persons?.map((p,i)=>{
                const pGeo=p.geo||resultsData.oGeos?.[p.station?.name];
                if(!pGeo)return null;
                return(<Polyline key={`dtl${i}-${expandedIdx}`} positions={[pGeo,activeMpGeo]}
                  pathOptions={{color:PERSON_COLORS[i],weight:3,opacity:.75,dashArray:'8 6'}}/>);
              })}
              {mps.map((mp,i)=>{
                const geo=resultsData.mpGeos?.[mp.station];
                if(!geo)return null;
                return(<Marker key={`dtmp${i}`} position={geo} icon={mkMeetDot(i+1,i===expandedIdx)}
                  eventHandlers={{click:()=>onCardExpand(i)}}><Popup>{mp.station}</Popup></Marker>);
              })}
            </>
          )}
          {/* Desktop single-origin radial tree */}
          {isResults&&!isZuzweit&&(originGeo||resultsData?.stationGeo)&&(()=>{
            const origin=originGeo||resultsData.stationGeo;
            const reTrains=trains.filter(t=>t.category==='RE');
            const rbsTrains=trains.filter(t=>['RB','S','ARV'].includes(t.category));
            const isRbsExp=dtExpandedClusters['__origin__'];
            return(<>
              {/* RE direct lines */}
              {reTrains.map((train,i)=>{
                const geo=dtDestGeos[train.finalDestination];
                if(!geo)return null;
                const isActive=trains.indexOf(train)===expandedIdx;
                return(<Polyline key={`dt-re-l-${train.id}`} positions={[origin,geo]}
                  pathOptions={{color:'#1D4ED8',weight:isActive?4:2.5,opacity:isActive?.95:.65}}/>);
              })}
              {reTrains.map((train,i)=>{
                const geo=dtDestGeos[train.finalDestination];
                if(!geo)return null;
                const idx=trains.indexOf(train);
                const isActive=idx===expandedIdx;
                return(<Marker key={`dt-re-d-${train.id}`} position={geo}
                  icon={mkDot('#1D4ED8',isActive?16:9)}
                  eventHandlers={{click:()=>setExpandedIdx(idx)}}>
                  <Popup>{train.finalDestination}</Popup>
                </Marker>);
              })}
              {/* RB/S cluster */}
              {rbsTrains.length>0&&(<>
                <Marker position={[origin[0]-0.025,origin[1]+0.05]}
                  icon={L.divIcon({className:'',iconSize:[68,22],iconAnchor:[0,11],
                    html:`<div style="background:${isRbsExp?'#7C3AED':'#f2e8f5'};color:${isRbsExp?'#fff':'#7C3AED'};border:1.5px solid #7C3AED;border-radius:100px;padding:2px 9px;font-size:10px;font-weight:600;white-space:nowrap;font-family:'DM Sans',sans-serif;box-shadow:0 2px 8px rgba(0,0,0,.18)">${isRbsExp?'▲ collapse':'RB / S +'+rbsTrains.length}</div>`})}
                  eventHandlers={{click:()=>{
                    const next=!dtExpandedClusters['__origin__'];
                    setDtExpandedClusters(prev=>({...prev,'__origin__':next}));
                    if(next){
                      const names=[...new Set(rbsTrains.map(t=>t.finalDestination))].slice(0,8);
                      geocodeBatch(names).then(g=>setDtDestGeos(prev=>({...prev,...g})));
                    }
                  }}}>
                  <Popup>Tap to show RB and S-Bahn trains</Popup>
                </Marker>
                {isRbsExp&&rbsTrains.map((train,i)=>{
                  const geo=dtDestGeos[train.finalDestination];
                  if(!geo)return null;
                  const col=train.category==='RB'?'#15803D':'#7C3AED';
                  const idx=trains.indexOf(train);
                  return([<Polyline key={`dt-rbs-l-${train.id||i}`} positions={[origin,geo]}
                    pathOptions={{color:col,weight:2,opacity:.6,dashArray:'6 4'}}/>,
                    <Marker key={`dt-rbs-d-${train.id||i}`} position={geo} icon={mkDot(col,8)}
                      eventHandlers={{click:()=>setExpandedIdx(idx)}}>
                      <Popup>{train.finalDestination}</Popup>
                    </Marker>]);
                })}
              </>)}
              {/* Hop 1: chains — origin→via (if not direct), then via→new dests branching */}
              {dtTreeData&&(()=>{
                const hop1RE=(dtTreeData.byHop[1]?.trains||[]).filter(t=>t.category==='RE');
                const directDests=new Set(trains.filter(t=>t.category==='RE').map(t=>t.finalDestination));
                const vias=[...new Set(hop1RE.map(t=>t.via))].slice(0,6);
                return vias.map((via,i)=>{
                  const vGeo=dtHopGeos[via];
                  if(!vGeo)return null;
                  const viaIsDirect=directDests.has(via);
                  const viaGeo=viaIsDirect?(dtDestGeos[via]||vGeo):vGeo;
                  const viaDests=hop1RE.filter(t=>t.via===via&&!directDests.has(t.finalDestination))
                    .filter((t,j,a)=>a.findIndex(x=>x.finalDestination===t.finalDestination)===j).slice(0,5);
                  const viaNonRE=(dtTreeData.byHop[1]?.trains||[]).filter(t=>t.via===via&&['RB','S','ARV'].includes(t.category));
                  const isExp=dtExpandedClusters[via];
                  return(<>
                    {!viaIsDirect&&<Polyline key={`dt-h1vl-${i}-${via}`} positions={[origin,vGeo]}
                      pathOptions={{color:'#1D4ED8',weight:2,opacity:.5,dashArray:'8 5'}}/>}
                    {!viaIsDirect&&<Marker key={`dt-h1vd-${i}-${via}`} position={vGeo} icon={mkDot('#1D4ED8',12)}>
                      <Popup><strong>{via}</strong><br/>Change here</Popup>
                    </Marker>}
                    {viaNonRE.length>0&&(
                      <Marker key={`dt-h1cl-${i}-${via}`}
                        position={[vGeo[0]+0.018,vGeo[1]+0.035]}
                        icon={L.divIcon({className:'',iconSize:[62,22],iconAnchor:[0,11],
                          html:`<div style="background:${isExp?'#15803D':'#e8f2ec'};color:${isExp?'#fff':'#15803D'};border:1.5px solid #15803D;border-radius:100px;padding:2px 9px;font-size:10px;font-weight:600;white-space:nowrap;font-family:'DM Sans',sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.18)">${isExp?'▲':'+'+viaNonRE.length+' RB/S'}</div>`})}
                        eventHandlers={{click:()=>dtExpandCluster(via,viaNonRE)}}>
                        <Popup>RB/S from {via}</Popup>
                      </Marker>
                    )}
                    {isExp&&viaNonRE.map((t,j)=>{
                      const dGeo=dtHopGeos[t.finalDestination];
                      if(!dGeo)return null;
                      const col=t.category==='RB'?'#15803D':'#7C3AED';
                      return([<Polyline key={`dt-h1rbl-${i}-${j}-${t.finalDestination}`} positions={[viaGeo,dGeo]}
                        pathOptions={{color:col,weight:1.5,opacity:.55,dashArray:'5 4'}}/>,
                        <Marker key={`dt-h1rbd-${i}-${j}-${t.finalDestination}`} position={dGeo} icon={mkDot(col,7)}>
                          <Popup>{t.finalDestination}</Popup>
                        </Marker>]);
                    })}
                    {viaDests.map((t,j)=>{
                      const dGeo=dtHopGeos[t.finalDestination];
                      if(!dGeo)return null;
                      return([<Polyline key={`dt-h1rel-${i}-${j}-${t.finalDestination}`} positions={[viaGeo,dGeo]}
                        pathOptions={{color:'#1D4ED8',weight:2,opacity:.5,dashArray:'6 4'}}/>,
                        <Marker key={`dt-h1red-${i}-${j}-${t.finalDestination}`} position={dGeo} icon={mkDot('#1D4ED8',9)}>
                          <Popup>{t.finalDestination}</Popup>
                        </Marker>]);
                    })}
                  </>);
                });
              })()}
              {/* Hop 2: via2→dest branches */}
              {dtTreeData&&(dtTreeData.byHop[2]?.trains||[])
                .filter(t=>t.category==='RE')
                .filter((t,i,a)=>a.findIndex(x=>x.finalDestination===t.finalDestination&&x.via===t.via)===i)
                .slice(0,20)
                .map((t,i)=>{
                  const via2Geo=dtHopGeos[t.via];
                  const destGeo=dtHopGeos[t.finalDestination];
                  if(!via2Geo||!destGeo)return null;
                  const directDests=new Set(trains.filter(tr=>tr.category==='RE').map(tr=>tr.finalDestination));
                  const hop1Dests=new Set((dtTreeData.byHop[1]?.trains||[]).filter(tr=>tr.category==='RE').map(tr=>tr.finalDestination));
                  if(directDests.has(t.finalDestination)||hop1Dests.has(t.finalDestination))return null;
                  return([
                    <Polyline key={`dt-h2-l-${i}-${t.finalDestination}`}
                      positions={[via2Geo,destGeo]}
                      pathOptions={{color:'#1D4ED8',weight:1.5,opacity:.35,dashArray:'4 7'}}/>,
                    <Marker key={`dt-h2-d-${i}-${t.finalDestination}`} position={destGeo}
                      icon={mkDot('#1D4ED8',7)}>
                      <Popup>{t.finalDestination}</Popup>
                    </Marker>
                  ]);
                })}

              <Marker position={origin} icon={mkDot('#D4521A',18)}>
                <Popup>{resultsData.station?.name}</Popup>
              </Marker>
            </>);
          })()}
        </MapContainer>
      </div>

      {/* ── Floating left panel — content-height, scrollable only when needed ── */}
      <div className="dt-panel">

        {/* Card 1: Logo + nav */}
        <div className="dt-card dt-card-nav">
          <span className="dt-logo" onClick={()=>{setStep(0);setResultsData(null);}}>zwischen</span>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button className="dt-nav-link" onClick={()=>setAboutOpen(true)}>About</button>
            <a className="dt-nav-link" href="/privacy.html" target="_blank" rel="noreferrer">Privacy</a>
            <a className="dt-nav-link" href="https://ko-fi.com/adityaaserkar" target="_blank" rel="noreferrer">☕ Support</a>
            <button className="hero-hamburger" style={{width:30,height:30}} onClick={()=>setMenuOpen(true)}><span/><span/><span/></button>
          </div>
        </div>

        {/* Card 2: Mode selector */}
        <div className="dt-card">
          <div className="dt-mode-grid">
            {t.modes.map(m=>(
              <button key={m.id}
                className={`dt-mode-card ${mode===m.id?'active':''}`}
                onClick={()=>{ if(m.soon){setDtComingSoon(m.id);} else {setMode(m.id);setResultsData(null);setStep(0);} }}>
                <span className="dt-mode-icon">{m.icon}</span>
                <div className="dt-mode-text">
                  <span className="dt-mode-label">{m.label}</span>
                  <span className="dt-mode-desc">{m.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Card 3: Input — always visible, dimmed after results */}
        <div className={`dt-card ${isResults?'dt-card-filled':''}`}>
          <DtInputForm mode={mode} dark={dark} t={t}
            initialStation={initialStation}
            isResults={isResults}
            onResults={data=>{setResultsData(data);setStep(2);}}/>
        </div>

        {/* Card 4: Results */}
        <AnimatePresence>
          {isResults&&(
            <motion.div className="dt-card dt-card-results"
              initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}
              exit={{opacity:0,y:8}} transition={{type:'spring',stiffness:300,damping:30}}>

              {isZuzweit&&(
                <>
                  {resultsData.hoursAhead>0&&(
                    <div className="hours-ahead-banner" style={{marginBottom:10}}>
                      <span className="hours-ahead-icon">🕐</span>
                      <span>{t.hoursAheadBanner(resultsData.hoursAhead)}</span>
                    </div>
                  )}
                  <div className="dt-results-label">{mps.length} meeting point{mps.length!==1?'s':''}</div>
                  {mps.map((mp,i)=>{
                    const spread=mp.spread;
                    const isExp=i===expandedIdx;
                    const shortName=mp.station.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'').trim();
                    const spreadLabel=spread!=null?(spread<=5?'Arrive together':`Within ${spread} min`):'Meeting point';
                    const pillCls=spread!=null&&spread<=10?'sp-good':spread!=null&&spread<=25?'sp-ok':'sp-late';
                    const pillTxt=spread!=null?(spread<=5?'Together':`${spread} min`):'—';
                    return(
                      <motion.div key={i} className={`dt-result-card ${isExp?'expanded':''}`}
                        layout transition={{type:'spring',stiffness:300,damping:30}}>
                        <div className="dt-result-header" onClick={()=>onCardExpand(i)}>
                          <div style={{flex:1,minWidth:0}}>
                            <div className="dt-result-name">{shortName.toUpperCase()}.</div>
                            <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>{spreadLabel}</div>
                          </div>
                          <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                            <span className={`sp-pill ${pillCls}`}>{pillTxt}</span>
                            <span style={{fontSize:11,color:'var(--faint)',transform:isExp?'rotate(180deg)':'none',transition:'transform .2s',display:'inline-block'}}>▾</span>
                          </div>
                        </div>
                        <AnimatePresence>
                          {isExp&&(
                            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}}
                              exit={{height:0,opacity:0}} style={{overflow:'hidden'}}
                              transition={{type:'spring',stiffness:300,damping:30}}>
                              <div style={{padding:'0 14px 14px'}}>
                                <div style={{height:1,background:'var(--bdr)',margin:'0 0 10px'}}/>
                                {mp.personDetails?.map((pd,pi)=>{
                                  const col=PERSON_COLORS[pi]||PERSON_COLORS[0];
                                  const train=pd.trains?.[0];
                                  return(
                                    <div key={pi} className="rc-person-row">
                                      <div className="rc-person-icon" style={{background:col+'22',color:col}}>{String.fromCharCode(65+pi)}</div>
                                      <div className="rc-person-body">
                                        <div className="rc-action-label">{pd.stationName}</div>
                                        {train&&<div className="rc-action-sub"><LineBadge line={train.line} category={train.category}/><span>dep {train.departure}</span>{pd.travelMins&&<span>· ~{pd.arrivalTime}</span>}</div>}
                                      </div>
                                    </div>
                                  );
                                })}
                                <div style={{height:1,background:'var(--bdr)',margin:'10px 0'}}/>
                                <div className="action-tiles">
                                  <a className="action-tile" href={taUrl(mp.station,'attractions')} target="_blank" rel="noreferrer"><span className="at-icon">🗺</span><span className="at-lbl">Explore</span></a>
                                  <a className="action-tile" href={taUrl(mp.station,'restaurants')} target="_blank" rel="noreferrer"><span className="at-icon">🍽</span><span className="at-lbl">Eat</span></a>
                                  <button className="action-tile" onClick={()=>onSaveToggle({id:mp.station,station:mp.station,line:'',category:''},null)}><span className="at-icon">♡</span><span className="at-lbl">Save</span></button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </>
              )}

              {!isZuzweit&&(
                <>
                  {loadingTrains&&<div className="loading-row"><div className="dots"><span/><span/><span/></div><span className="loading-txt">Loading trains…</span></div>}
                  {!loadingTrains&&trains.map((train,i)=>{
                    const isExp=i===expandedIdx;
                    const dest=train.finalDestination.replace(/ (Hbf|Hauptbahnhof|Bahnhof).*/,'');
                    return(
                      <motion.div key={`card-${train.id}`} className={`dt-result-card ${isExp?'expanded':''}`}
                        layout transition={{type:'spring',stiffness:300,damping:30}}>
                        <div className="dt-result-header" onClick={()=>setExpandedIdx(i)}>
                          <div style={{flex:1,minWidth:0}}>
                            <div className="dt-result-name">{dest.toUpperCase()}.</div>
                            <div style={{display:'flex',alignItems:'center',gap:5,marginTop:2}}>
                              <LineBadge line={train.line} category={train.category}/>
                              <span style={{fontSize:11,color:'var(--muted)'}}>dep {train.departure}{train.laterCount>0&&<span style={{color:'var(--faint)'}}> · +{train.laterCount} later</span>}</span>
                            </div>
                          </div>
                          <span style={{fontSize:11,color:'var(--faint)',transform:isExp?'rotate(180deg)':'none',transition:'transform .2s',display:'inline-block',flexShrink:0}}>▾</span>
                        </div>
                        <AnimatePresence>
                          {isExp&&(
                            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}}
                              exit={{height:0,opacity:0}} style={{overflow:'hidden'}}
                              transition={{type:'spring',stiffness:300,damping:30}}>
                              <div style={{padding:'0 14px 14px'}}>
                                <div style={{height:1,background:'var(--bdr)',margin:'0 0 8px'}}/>
                                <div style={{fontSize:11,color:'var(--muted)',marginBottom:10,lineHeight:1.6}}>
                                  {train.allStops.slice(0,5).join(' · ')}{train.allStops.length>5&&` +${train.allStops.length-5} more`}
                                </div>
                                <div className="action-tiles">
                                  <a className="action-tile" href={taUrl(train.finalDestination,'attractions')} target="_blank" rel="noreferrer"><span className="at-icon">🗺</span><span className="at-lbl">Explore</span></a>
                                  <a className="action-tile" href={taUrl(train.finalDestination,'restaurants')} target="_blank" rel="noreferrer"><span className="at-icon">🍽</span><span className="at-lbl">Eat</span></a>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Desktop input form ────────────────────────────────────────────────────────
function DtInputForm({mode,dark,t,onResults,isResults,initialStation}){
  const isZuzweit=mode==='zuzweit';
  const [queryA,setQueryA]=useState('');
  const [stationA,setStationA]=useState(null);
  const [stationAGeo,setStationAGeo]=useState(null);
  const [persons,setPersons]=useState([
    {id:0,query:'',station:null,geo:null},
    {id:1,query:'',station:null,geo:null},
  ]);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [hops,setHops]=useState(0);

  // DtInputForm prefill from landing page
  useEffect(()=>{
    if(!initialStation)return;
    setQueryA(initialStation);
    apiFetch(`${API_BASE}/stations?q=${encodeURIComponent(initialStation)}`)
      .then(r=>r.json())
      .then(d=>{const s=d.stations&&d.stations[0];if(s){setStationA(s);setQueryA(s.name);}})
      .catch(()=>{});
  },[initialStation]);

  const ready=isZuzweit?persons.filter(p=>p.station).length>=2:!!stationA;

  const selectSingle=async s=>{
    setStationA(s);setQueryA(s.name);
    const geo=await geocodeOne(s.name);
    if(geo)setStationAGeo(geo);
  };
  const selectPerson=async(idx,s)=>{
    setPersons(prev=>prev.map((p,i)=>i===idx?{...p,query:s.name,station:s}:p));
    const geo=await geocodeOne(s.name);
    if(geo)setPersons(prev=>prev.map((p,i)=>i===idx?{...p,geo}:p));
  };

  const handleSubmit=async()=>{
    if(!ready||loading)return;
    setLoading(true);setError('');
    try{
      if(isZuzweit){
        const valid=persons.filter(p=>p.station);
        const r=await apiFetch(`${API_BASE}/midpoint?evas=${valid.map(p=>p.station.eva).join(',')}&hops=${hops}`);
        if(!r.ok)throw new Error(`HTTP ${r.status}`);
        const d=await r.json();
        const mps=d.meetingPoints||[];
        if(!mps.length){setError(t.noResults);setLoading(false);return;}
        const mpNames=mps.map(m=>m.station).slice(0,10);
        const originNames=valid.map(p=>p.station.name);
        const allG=await geocodeBatch([...originNames,...mpNames]);
        const mpGeos={};mpNames.forEach(n=>{if(allG[n])mpGeos[n]=allG[n];});
        const oGeos={};originNames.forEach(n=>{if(allG[n])oGeos[n]=allG[n];});
        const personsWithGeo=persons.map(p=>({...p,geo:p.geo||oGeos[p.station?.name]||null}));
        const all=Object.values(allG);
        onResults({mode,persons:personsWithGeo,mps,mpGeos,oGeos,
          mapFit:all.length>=2?[[Math.min(...all.map(c=>c[0]))-.1,Math.min(...all.map(c=>c[1]))-.15],[Math.max(...all.map(c=>c[0]))+.1,Math.max(...all.map(c=>c[1]))+.15]]:null,
          hoursAhead:d.hoursAhead||0});
      } else {
        // Pass results immediately, geocode async in background
        onResults({mode,station:stationA,hops,hourOffset:0,stationGeo:stationAGeo});
        // If geo not ready yet, fetch it and update results
        if(!stationAGeo) {
          geocodeOne(stationA.name).then(geo=>{
            if(geo) onResults({mode,station:stationA,hops,hourOffset:0,stationGeo:geo});
          });
        }
      }
    }catch(e){setError(e.message);}
    setLoading(false);
  };

  return(
    <div className="dt-input-form">
      {/* Station inputs — always visible, dimmed when results shown */}
      <div className={`dt-inputs-wrap ${isResults?'dt-inputs-filled':''}`}>
        {isZuzweit?(
          <>
          <div style={{display:'flex',gap:8,marginBottom:8}}>
            {persons.slice(0,2).map((p,i)=>(
              <div key={p.id} className={`station-card ${p.station?'filled':''}`} style={{flex:1,minWidth:0}}>
                <span className="station-card-lbl">
                  <span className="station-card-dot" style={{background:PERSON_COLORS[i]}}/>
                  {i===0?'A':'B'}
                </span>
                <StationInput value={p.query}
                  onChange={v=>setPersons(prev=>prev.map((x,xi)=>xi===i?{...x,query:v,station:v===x.station?.name?x.station:null}:x))}
                  onSelect={s=>selectPerson(i,s)}
                  placeholder={i===0?'e.g. München':'e.g. Berlin'}
                  color={PERSON_COLORS[i]}/>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:5,marginBottom:2}}>
            {[{v:0,l:'Direct'},{v:1,l:'+1 change'},{v:2,l:'+2 changes'}].map(h=>(
              <button key={h.v}
                onClick={()=>setHops(h.v)}
                style={{flex:1,padding:'5px 4px',borderRadius:8,border:`1.5px solid ${hops===h.v?'var(--ink)':'var(--bdr2)'}`,background:hops===h.v?'var(--ink)':'var(--surf2)',color:hops===h.v?'var(--bg)':'var(--muted)',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",transition:'all .13s'}}>
                {h.l}
              </button>
            ))}
          </div>
          </>
        ):(
          <>
          <div className={`station-card ${stationA?'filled':''}`} style={{marginBottom:8}}>
            <span className="station-card-lbl">
              <span className="station-card-icon">⊙</span>
              {t.startingFrom}
            </span>
            <StationInput value={queryA} onChange={v=>{setQueryA(v);setStationA(prev=>prev?.name===v?prev:null);}} onSelect={selectSingle}
              placeholder={t.placeholder}/>
          </div>
          {mode!=='tagesausflug'&&(
          <div style={{display:'flex',gap:5,marginBottom:2}}>
            {[{v:0,l:'Direct'},{v:1,l:'+1 change'},{v:2,l:'+2 changes'}].map(h=>(
              <button key={h.v}
                onClick={()=>setHops(h.v)}
                style={{flex:1,padding:'5px 4px',borderRadius:8,border:`1.5px solid ${hops===h.v?'var(--ink)':'var(--bdr2)'}`,background:hops===h.v?'var(--ink)':'var(--surf2)',color:hops===h.v?'var(--bg)':'var(--muted)',fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",transition:'all .13s'}}>
                {h.l}
              </button>
            ))}
          </div>
          )}
          </>
        )}
      </div>
      {error&&<div className="error-msg" style={{margin:'6px 0'}}>{error}</div>}
      {!ready&&!isZuzweit&&queryA.trim().length>=2&&(
        <div className="error-msg" style={{margin:'6px 0',color:'var(--faint)'}}>{t.selectHint}</div>
      )}
      <motion.button className="sheet-cta" style={{marginTop:8}}
        onClick={handleSubmit} disabled={!ready||loading}
        whileTap={{scale:.97}}
        animate={{background:ready?'var(--accent)':'var(--surf3)',color:ready?'#fff':'var(--faint)'}}
        transition={{duration:.2}}>
        {loading
          ?<span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
              <span className="dots" style={{display:'inline-flex',gap:4}}><span/><span/><span/></span>Finding…
            </span>
          :<>{isZuzweit?t.findMidpoint:t.explore} →</>
        }
      </motion.button>
    </div>
  );
}


// ─── Desktop detection ───────────────────────────────────────────────────────
function useDesktop(){
  const [isDesktop,setIsDesktop]=useState(()=>window.innerWidth>=768);
  useEffect(()=>{
    const h=()=>setIsDesktop(window.innerWidth>=768);
    window.addEventListener('resize',h,{passive:true});
    return()=>window.removeEventListener('resize',h);
  },[]);
  return isDesktop;
}

// ══════════════════════════════════════════════════════════════════════════════
// ─── ROOT APP ─────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export default function App(){
  const _landingData=(()=>{
    try{
      // Try sessionStorage first (set by landing page before navigating)
      const s=sessionStorage.getItem('zwischen-landing-station');
      if(s){sessionStorage.removeItem('zwischen-landing-station');return JSON.parse(s);}
      // Fallback: read URL hash #station=X&mode=Y
      const hash=window.location.hash.slice(1);
      if(hash){
        const p=Object.fromEntries(hash.split('&').map(kv=>kv.split('=')));
        if(p.station)return{name:decodeURIComponent(p.station),mode:p.mode||'soweit'};
      }
    }catch(e){}
    return null;
  })();
  // Read station name from hash for context transfer
  const initialStation = (()=>{
    try{
      const h=window.location.hash.slice(1);
      if(h){
        const p=Object.fromEntries(h.split('&').map(kv=>kv.split('=')));
        if(p.station)return decodeURIComponent(p.station);
      }
    }catch(e){}
    return _landingData?.name||null;
  })();
  const [dark,setDark]=useState(false);
  const [lang,setLang]=useState('en');
  const [mode,setMode]=useState(_landingData?.mode||'soweit');
  const [step,setStep]=useState(0); // 0=hero 1=map 2=results
  const [resultsData,setResultsData]=useState(null);
  const [savedTrips,setSavedTrips]=useState(()=>{try{return JSON.parse(localStorage.getItem('zwischen-saved')||'[]');}catch{return[];}});

  // Read station pre-selected from landing page
  const [menuOpen,setMenuOpen]=useState(false);
  const [aboutOpen,setAboutOpen]=useState(false);
  const [showSaved,setShowSaved]=useState(false);

  const t=T[lang];

  useEffect(()=>{ document.documentElement.setAttribute('data-theme',dark?'dark':'light'); },[dark]);
  // Keep <html lang> in sync so screen readers pronounce content correctly
  useEffect(()=>{ document.documentElement.lang=lang; },[lang]);

  const handleModeSelect=id=>{
    if(id==='about'){setAboutOpen(true);return;}
    setMode(id);setStep(0);setResultsData(null);
  };

  const handleResults=data=>{
    setResultsData({...data});
    setStep(2);
  };

  const toggleSave=(train,fromStation)=>{
    setSavedTrips(prev=>{
      const next=prev.find(t=>t.id===train.id)?prev.filter(t=>t.id!==train.id):[...prev,{...train,fromStation}];
      try{localStorage.setItem('zwischen-saved',JSON.stringify(next));}catch{}
      return next;
    });
  };

  // Persistent Leaflet map instance (avoids remount)
  const [mapMounted,setMapMounted]=useState(false);

  const isDesktop=useDesktop();

  if(isDesktop){
    return(
      <div className={`app-root ${dark?'dark':''}`}>
        <DesktopLayout
          initialStation={initialStation}
          mode={mode} setMode={setMode}
          step={step} setStep={setStep}
          resultsData={resultsData} setResultsData={setResultsData}
          savedTrips={savedTrips} onSaveToggle={toggleSave}
          dark={dark} t={t} lang={lang} setLang={setLang}
          menuOpen={menuOpen} setMenuOpen={setMenuOpen}
          aboutOpen={aboutOpen} setAboutOpen={setAboutOpen}/>
      </div>
    );
  }

  return(
    <div className={`app-root ${dark?'dark':''}`}>
      <div className="app-frame">

        <HamburgerMenu open={menuOpen} onClose={()=>setMenuOpen(false)}
          lang={lang} setLang={setLang} dark={dark} setDark={setDark}
          onMode={handleModeSelect} t={t}
          savedTrips={savedTrips} onSavedOpen={()=>setShowSaved(true)}/>
        <AboutModal open={aboutOpen} onClose={()=>setAboutOpen(false)} t={t}/>



        {showSaved&&<SavedTripsSheet trips={savedTrips} onRemove={t=>toggleSave(t,t.fromStation)} onClose={()=>setShowSaved(false)} t={t}/>}
        <div className="screen-area">
          {/* mode="wait": the outgoing step fully exits before the next enters.
              Without it, hero + map rendered stacked semi-transparent during
              the crossfade — the "double exposure" seen on mobile, replayed on
              every keyboard open/close (viewport resize). */}
          <AnimatePresence mode="wait" initial={false}>
            {step===0&&(
              <StepHero key="s0" mode={mode} setMode={setMode}
                onContinue={()=>setStep(1)}
                savedTrips={savedTrips} t={t}
                menuOpen={menuOpen} setMenuOpen={setMenuOpen}/>
            )}
            {step===1&&(
              <StepMap key="s1" mode={mode} setMode={setMode} dark={dark} t={t} initialStation={initialStation}
                onResults={handleResults}
                onBack={()=>setStep(0)}/>
            )}
            {step===2&&resultsData&&(
              resultsData.mode==='tagesausflug'&&resultsData.station&&resultsData.stationGeo
                ? <GTFSExplorer key="s2-gtfs"
                    originStation={{ name: resultsData.station.name, eva: resultsData.station.eva, lat: resultsData.stationGeo[0], lng: resultsData.stationGeo[1] }}
                    apiFetch={apiFetch}
                    API_BASE={API_BASE}
                    lang={lang} setLang={setLang}
                    onModeChange={(m)=>{ setMode(m); setStep(0); setResultsData(null); }}/>
                : <StepResults key="s2" data={resultsData} dark={dark} t={t}
                    savedTrips={savedTrips} onSaveToggle={toggleSave}
                    onBack={()=>setStep(1)}
                    onHome={()=>{setStep(0);setResultsData(null);}}/>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}